import { useFotos } from "../../../appMovil/fotos";
import { useSaltoACita } from "./saltoACita";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { RootState } from "../../../globals";
import { guardarBorrador, leerBorrador, useBorrador } from "./borradores";
import HttpManager from "../../../services/HttpManager";
import { EquipoCanal, EquipoCanalInfo, EquipoMensaje, Sticker } from "../../../services/v2/prospects.service";
import { telefonoBonito } from "../../../utils/telefonoBonito";
import { abrirArchivo } from "../../../components/enrollment/visorArchivo";
import Adjuntos from "./adjuntos";
import WappIcono from "../../../components/wappIcono";
import { cuando, diaClave, diaEtiqueta, errorTexto, hora, iniciales, nombreCompleto, tonoAvatar } from "./salesHelpers";
import { intervaloRespaldo, push } from "./push";
import { equipoVista } from "./equipoAvisos";
import AvisosIaView from "./avisosIaView";
import TextoRico from "./textoRico";
import PlantelSelect, { PlantelOpcion } from "./plantelSelect";
import { Album, agruparAlbumes, BandejaEnvio, Elegido, elegidosDe, enviarVarios, estadoPalomitas, MAX_ARCHIVOS, MedioMensaje, Palomitas, PastillaReacciones, useArchivosEntrantes } from "./equipoMedios";
import { activarAvisos } from "../../../appMovil/appMovil";
import IconoCanalPicker, { IconoCanal } from "./iconoCanalPicker";
import { BotonEmoji, EmojisFlotante, insertarEnCursor, teclaEnter } from "./equipoEmojis";
import { idsMencionados, ListaMenciones, Persona, primerNombre, useMenciones } from "./menciones";
import { guardarSticker, IconoSticker } from "./stickers";
import { BotonGif, conEmojisCliq, GifMensaje, gifDe, resumenTexto } from "./equipoGifs";
import EquipoReenviar from "./equipoReenviar";
import { BotonLlamar, BurbujaLlamada, LlamadaViva, esMensajeLlamada } from "./llamadasEquipo";
import { useCrecer } from "./crecer";
import { BarraSeleccion, MarcaSeleccion, copiarTexto } from "./seleccion";
import { BuscadorChat, ItemBusqueda, Resaltado, sinAcentos, useBuscarDiferido } from "./buscarMensajes";
import { EquipoFavorito, EquipoFijado, EquipoHiloResumen, EquipoResultado, EstadoEquipo } from "../../../services/v2/prospects.service";
import { Reloj, useEnCamino } from "./equipoEnCamino";
import PerfilPersona, { AnclaPerfil } from "./perfilPersona";
import HiloPanel, { HilosVista, ResumenHilo } from "./hiloPanel";
import { ActividadVista, AdjuntosPanel, EncuestaMensaje, EncuestaModal, EstadoMenu, fechaProgramada, hastaTexto, MenuHoras, OPCIONES_PROGRAMAR, OPCIONES_RECORDAR, SaltoRapido } from "./equipoExtras";

interface Props {
	campusId: number;
	/** Abrir a un cliente compartido en su conversación de WappChat. */
	onAbrirCliente: (prospectId: number) => void;
	/** Cuántos no leídos quedan (para el botón Equipo de arriba). */
	onNoLeidos?: (n: number) => void;
	/** Conversación que hay que abrir (?c=N: desde una ventanita de abajo o un aviso). */
	pedido?: number | null;
	/** Hilo que hay que abrir (?h=N: desde el aviso de una respuesta en hilo). */
	pedidoHilo?: number | null;
}


/**
 * "equipo_visto" (alguien recibió o leyó) solo mueve MIS palomitas. Llega una vez por cada persona del canal y por cada
 * mensaje (12-oct-2026: un canal de 10 personas pedía la conversación ~35 veces por mensaje). Si lo mío ya lo leyeron
 * todas (vistoHasta = lo mínimo leído por las demás), no hay nada que pedir.
 */
export const vistoCambiaAlgo = (mensajes: EquipoMensaje[], yoId: number | null | undefined, vistoHasta: number | null): boolean => {
	if (!yoId) return true;   // sin saber quién soy: como antes
	const mio = mensajes.reduce((n, m: any) => (m.user_id === yoId && !m.importado && !m.deleted_at && m.id > n ? m.id : n), 0);
	return !!mio && (vistoHasta == null || mio > vistoHasta);
};

/** The emoji bar opens above the bubble; at the top of the chat it would be cut off, so there it opens below. */
export const barraAbajo = (boton: HTMLElement): boolean => {
	const msg = boton.closest(".eq-msg, .wtd-msg");
	const cont = boton.closest(".eq-mensajes, .wtd-msgs");
	return !!msg && !!cont && msg.getBoundingClientRect().top - cont.getBoundingClientRect().top < 50;
};
export const EMOJIS = ["👍", "❤️", "😂", "😮", "🙏", "✅"];
const tamano = (b: number | null) => (!b ? "" : b >= 1048576 ? (b / 1048576).toFixed(1) + " MB" : Math.max(1, Math.round(b / 1024)) + " KB");

/** Texto del mensaje: ligas, @menciones y clientes (las ligas de Zoho CRM llegan del back como marcador de cliente). */
export const Texto: React.FC<{ t: string }> = ({ t }) => {
	// GIF (liga de Tenor/GIPHY/KLIPY, como llegó de Cliq o del buscador): animado, y el resto del texto debajo.
	const g = gifDe(t);
	if (g) return <>{<GifMensaje url={g.url} />}{g.resto ? <TextoRico t={conEmojisCliq(g.resto)} cliq /> : null}</>;
	return <TextoRico t={conEmojisCliq(t)} cliq />;
};
/** El mensaje se pinta como medio (burbuja sin relleno): archivo, álbum o GIF. */
export const esMedio = (m: EquipoMensaje, album?: unknown): boolean => !!album || (!!m.file_tipo && m.file_tipo !== "audio" && !m.deleted_at) || (!m.deleted_at && !!gifDe(m.body));

/** Foto, nota de voz, video o documento de un mensaje. Fotos, voz y video se piden solos; documentos al tocarlos. */
export const Archivo: React.FC<{ campusId: number; m: EquipoMensaje }> = ({ campusId, m }) => {
	const http = HttpManager.getInstance();
	const [url, setUrl] = useState<string | null>(null);
	const [err, setErr] = useState<string | null>(null);
	const pedir = useCallback(async () => {
		try { const r = await http.prospectsService.equipoArchivo(campusId, m.id); setUrl(r.data.url); return r.data; } catch (e) { setErr(errorTexto(e)); return null; }
	}, [campusId, m.id]);
	useEffect(() => { if (m.file_tipo !== "document") pedir(); }, [m.id]);
	const ver = () => abrirArchivo({ origen: "WappTeam",
		nombre: m.file_name || "Archivo", urlLista: m.file_tipo === "image" ? url : null,
		cargar: async () => {
			const d = url ? { url } : await pedir();
			const blob = await (await fetch((d as any).url)).blob();
			return { blob, nombre: m.file_name };
		},
	});
	if (err) return <span className="cv-adj-error">{err}</span>;
	if (m.file_tipo === "image") return url ? <img className="eq-foto" src={url} alt={m.file_name || "Foto"} onClick={ver} /> : <span className="apagado">Cargando foto…</span>;
	if (m.file_tipo === "audio") return url ? <audio className="eq-audio" controlsList="nodownload" controls preload="metadata" src={url} /> : <span className="apagado">Cargando nota de voz…</span>;
	if (m.file_tipo === "video") return url ? <video className="eq-foto" controlsList="nodownload" controls playsInline preload="metadata" src={url + "#t=0.001"} /> : <span className="apagado">Cargando video…</span>;
	return (
		<button className="eq-doc" onClick={ver}>
			<span className="ext">{(m.file_name || "").split(".").pop()?.toUpperCase().slice(0, 4) || "DOC"}</span>
			<span><b>{m.file_name || "Documento"}</b><small>{tamano(m.file_size)}</small></span>
		</button>
	);
};

type Panel = null | "nuevoCanal" | "nuevoGrupo" | "directo" | "explorar" | "miembros" | "ajustes" | "supervision" | "favoritos";

/** Una línea para un fijado o un favorito: el texto, o qué clase de archivo es. */
const textoCorto = (body: string | null, fileTipo: string | null, fileName: string | null, prospectId?: number | null): string =>
	resumenTexto(body) || (fileTipo === "audio" ? "Nota de voz" : fileTipo === "image" ? "Foto" : fileTipo === "video" ? "Video" : fileTipo === "sticker" ? "Sticker"
		: fileTipo ? fileName || "Archivo" : prospectId ? "Cliente compartido" : "Mensaje");

/**
 * Equipo (Esteban, 2026-10-04: "lo más parecido a Cliq posible"): el chat interno de la escuela dentro de WappChat.
 * Canales automáticos por rol, canales públicos y privados, canales de anuncios, grupos y mensajes directos; archivos,
 * fotos, video y notas de voz; ligas, menciones, responder, reacciones, editar, borrar, buscar y "visto".
 */
const EquipoView: React.FC<Props> = ({ campusId, onAbrirCliente, onNoLeidos, pedido, pedidoHilo }) => {
	const fotos = useFotos();
	const [masCab, setMasCab] = useState<boolean>(false);
	const http = HttpManager.getInstance();
	const yoId: number | null = useSelector((state: RootState) => (state.rootReducer.sessionState as any).user?.id ?? null);
	// Avisos de IA (solo Super Admin): todo lo que manda IA Washington, en el panel de la derecha.
	const perfil: any = useSelector((state: RootState) => (state as any).userProfile?.value);
	const esSuper = perfil?.currentRole === "superAdmin";
	const navigate = useNavigate();
	const [vistaIa, setVistaIa] = useState<boolean>(false);
	const [canales, setCanales] = useState<EquipoCanal[]>([]);
	const [cargado, setCargado] = useState<boolean>(false);
	const [abierto, setAbierto] = useState<number | null>(null);
	const [canal, setCanal] = useState<EquipoCanalInfo | null>(null);
	const [mensajes, setMensajes] = useState<EquipoMensaje[]>([]);
	const mensajesRef = useRef<EquipoMensaje[]>([]);
	mensajesRef.current = mensajes;
	const [hayMas, setHayMas] = useState<boolean>(false);
	const [vistoHasta, setVistoHasta] = useState<number | null>(null);
	const vistoRef = useRef<number | null>(null);
	vistoRef.current = vistoHasta;
	const vistoTimer = useRef<any>(null);
	const [entregadoHasta, setEntregadoHasta] = useState<number | null>(null);
	// Fijados (Esteban, 2026-10-16): arriba de la conversación, uno a la vez; tocarlo lleva al mensaje y pasa al siguiente.
	const [fijados, setFijados] = useState<EquipoFijado[]>([]);
	const [fijIdx, setFijIdx] = useState<number>(0);
	const [fijando, setFijando] = useState<number | null>(null);
	// Favoritos: los mensajes con estrella, de todas mis conversaciones o solo de una (favCanal).
	const [favoritos, setFavoritos] = useState<EquipoFavorito[] | null>(null);
	const [favCanal, setFavCanal] = useState<number | null>(null);
	// Varios archivos a la vez (bandeja antes de mandar) e "Info del mensaje".
	const [elegidos, setElegidos] = useState<Elegido[]>([]);
	const [progresoArch, setProgresoArch] = useState<{ i: number; n: number; f: number } | null>(null);
	const [errorArch, setErrorArch] = useState<string | null>(null);
	const [info, setInfo] = useState<{ id: number; datos: any | null } | null>(null);
	// Reenviar: el mensaje (o las fotos del álbum) que se está reenviando.
	const [reenviando, setReenviando] = useState<EquipoMensaje[] | null>(null);
	const [eligiendoIcono, setEligiendoIcono] = useState(false);
	useEffect(() => setEligiendoIcono(false), [abierto]);
	const zonaChat = useRef<HTMLDivElement>(null);
	// Borrador por canal (borradores.ts), el mismo de la ventanita. Al editar un mensaje ya enviado el cuadro muestra ese
	// mensaje sin pisar el borrador; al guardar o cancelar la edición vuelve el borrador.
	const [texto, setTextoBorrador, borrador] = useBorrador(abierto ? "team:" + abierto : null);
	const [respondiendo, setRespondiendo] = useState<EquipoMensaje | null>(null);
	const [editando, setEditando] = useState<EquipoMensaje | null>(null);
	const editandoRef = useRef<EquipoMensaje | null>(null);
	editandoRef.current = editando;
	const setTexto = (t: string) => (editando ? borrador.soloPantalla(t) : setTextoBorrador(t));
	// Las @menciones del borrador también se guardan: sin ellas, al volver y enviar no le llegaba el aviso a la persona.
	const [menciones, setMenciones] = useState<{ id: number; nombre: string }[]>([]);
	const mencionesDe = (canal: number) => { try { return JSON.parse(leerBorrador(yoId, "eqm:" + canal) || "[]"); } catch { return []; } };
	const canalMenciones = useRef<number | null>(null);
	useEffect(() => { if (abierto && canalMenciones.current === abierto) guardarBorrador(yoId, "eqm:" + abierto, menciones.length ? JSON.stringify(menciones) : ""); }, [menciones]); // eslint-disable-line react-hooks/exhaustive-deps
	const [escribe, setEscribe] = useState<string | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [adjOcupado, setAdjOcupado] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [panel, setPanel] = useState<Panel>(null);
	const [menuNuevo, setMenuNuevo] = useState<boolean>(false);
	// Esc cierra el ⋮ del encabezado y el menú "+ Nuevo" (Esteban, 2026-10-22).
	useEffect(() => {
		if (!masCab && !menuNuevo) return;
		const k = (e: KeyboardEvent) => { if (e.key === "Escape") { setMasCab(false); setMenuNuevo(false); } };
		document.addEventListener("keydown", k);
		return () => document.removeEventListener("keydown", k);
	}, [masCab, menuNuevo]);
	const [reaccionando, setReaccionando] = useState<number | null>(null);
	const [reacAbajo, setReacAbajo] = useState<boolean>(false);
	// "+" de las reacciones: el panel completo de emojis para reaccionar con cualquiera.
	const [masReac, setMasReac] = useState<EquipoMensaje | null>(null);
	// Celular: tocar la burbuja enseña sus acciones (en computadora salen al pasar el mouse).
	const [elegidoMsg, setElegidoMsg] = useState<number | null>(null);
	// Mantener presionado (celular): enseña las acciones de cualquier mensaje, también de fotos, videos y stickers, que al
	// tocarlos abren el visor (Esteban, 2026-10-16: "no se puede responder a mensajes que solo son imagen o sticker").
	const presion = useRef<{ t: any; x: number; y: number; id: number | null }>({ t: null, x: 0, y: 0, id: null });
	const presionar = (m: EquipoMensaje, e: React.TouchEvent) => {
		clearTimeout(presion.current.t);
		const p = e.touches[0];
		presion.current = { x: p.clientX, y: p.clientY, id: null, t: setTimeout(() => {
			presion.current.id = m.id; setElegidoMsg(m.id); setReaccionando(null); setFijando(null);
			try { navigator.vibrate?.(12); } catch { /* sin vibración */ }
		}, 450) };
	};
	const soltarPresion = (e?: React.TouchEvent) => {
		if (e && e.type === "touchmove") {
			const p = e.touches[0];
			if (Math.abs(p.clientX - presion.current.x) < 10 && Math.abs(p.clientY - presion.current.y) < 10) return;   // dedo quieto: sigue contando
		}
		clearTimeout(presion.current.t);
	};
	// Selección múltiple (Esteban, 2026-10-12): cabeza del mensaje (o del álbum) -> sus mensajes. null = no se está eligiendo.
	const [sel, setSel] = useState<Record<number, EquipoMensaje[]> | null>(null);
	const [reenvioNuevo, setReenvioNuevo] = useState<boolean>(false);
	useEffect(() => { setSel(null); }, [abierto]);
	const alternarSel = (m: EquipoMensaje, grupo: EquipoMensaje[]) => setSel((prev) => {
		const o = { ...(prev || {}) };
		if (o[m.id]) delete o[m.id]; else o[m.id] = grupo;
		return o;
	});
	const elegidosSel = (): EquipoMensaje[] => Object.keys(sel || {}).map(Number).sort((a, b) => a - b).flatMap((k) => (sel || {})[k]);
	const textoSel = () => elegidosSel().map((x) => resumenTexto(x.body)).filter(Boolean).join("\n");
	// formularios de los paneles
	const [fNombre, setFNombre] = useState("");
	const [fDesc, setFDesc] = useState("");
	const [fPublico, setFPublico] = useState(false);
	const [fAnuncios, setFAnuncios] = useState(false);
	const [fBusca, setFBusca] = useState("");
	const [fGente, setFGente] = useState<{ id: number; nombre: string }[]>([]);
	const [personas, setPersonas] = useState<{ id: number; nombre: string; rol: string | null; planteles?: string[]; plantel?: string | null }[]>([]);
	// Directorio por planteles: solo gente de mis planteles; "Todos mis planteles" por defecto.
	const [fPlantel, setFPlantel] = useState<number | null>(null);
	const [misPlanteles, setMisPlanteles] = useState<PlantelOpcion[]>([]);
	const [explorar, setExplorar] = useState<{ id: number; nombre: string; descripcion: string | null; anuncios: boolean; miembros: number }[]>([]);
	// Buscar (Esteban, 2026-10-12): la caja de arriba de la lista filtra conversaciones y busca mensajes en todas; la lupa
	// del encabezado busca solo en la abierta. Un resultado abre la conversación en ese mensaje y lo resalta.
	const [buscaLista, setBuscaLista] = useState<string>("");
	const [buscaCanal, setBuscaCanal] = useState<boolean>(false);
	const [saltoEq, setSaltoEq] = useState<{ id: number; n: number } | null>(null);
	const saltoPendiente = useRef<{ canal: number; id: number } | null>(null);
	const [miembros, setMiembros] = useState<{ id: number; nombre: string; admin: boolean }[]>([]);
	// Mi equipo (Esteban, 2026-10-09): la jefa y las de arriba leen las conversaciones de su gente.
	const [supGente, setSupGente] = useState<{ id: number; name: string }[]>([]);
	const [supPersona, setSupPersona] = useState<number | null>(null);
	const [supCanales, setSupCanales] = useState<{ id: number; tipo: string; nombre: string; last_message_at: string }[]>([]);
	const [avisos, setAvisos] = useState<string>(() => (typeof Notification !== "undefined" ? Notification.permission : "denied"));
	const abiertoRef = useRef<number | null>(null);
	abiertoRef.current = abierto;
	useEffect(() => { if (pedido) setAbierto(pedido); }, [pedido]);
	// Hilo abierto a la derecha (como Slack) y cuántas veces llegó un aviso suyo (para que el panel se vuelva a pedir).
	const [hilo, setHilo] = useState<number | null>(null);
	const [hiloVersion, setHiloVersion] = useState(0);
	const hiloRef = useRef<number | null>(null);
	hiloRef.current = hilo;
	const hiloPendiente = useRef<number | null>(null);
	useEffect(() => { if (pedidoHilo) hiloPendiente.current = pedidoHilo; }, [pedidoHilo]);
	// "Hilos" arriba de la lista: los que sigo, con cuántas respuestas nuevas (y el numerito por mensaje en la conversación).
	const [hilos, setHilos] = useState<EquipoHiloResumen[] | null>(null);
	const [hilosTotal, setHilosTotal] = useState(0);
	const [vistaHilos, setVistaHilos] = useState(false);
	const cargarHilos = useCallback(async () => {
		try {
			const r = await http.prospectsService.equipoHilos(campusId);
			setHilos(r.data.hilos);
			setHilosTotal(r.data.nuevas);
		} catch { /* sin hilos: no estorba */ }
	}, [campusId]);
	useEffect(() => { cargarHilos(); }, [cargarHilos]);
	const hilosNuevas: Record<number, number> = {};
	(hilos || []).forEach((x) => { if (x.nuevas) hilosNuevas[x.id] = x.nuevas; });
	useEffect(() => { if (abierto) setVistaIa(false); }, [abierto]);
	// Lo que está a la vista no avisa (los avisos de toda la app viven en equipoAvisos/wappteamDock).
	useEffect(() => { equipoVista.poner(abierto); return () => equipoVista.quitar(abierto); }, [abierto]);
	const fin = useRef<HTMLDivElement>(null);
	const caja = useRef<HTMLTextAreaElement>(null);
	useCrecer(caja, texto);
	const ultimoEscribe = useRef<number>(0);
	const escribeTimer = useRef<any>(null);

	const cargarCanales = useCallback(async () => {
		try {
			const r = await http.prospectsService.equipoCanales(campusId);
			setCanales(r.data.canales); setCargado(true);
		} catch (e) { setError(errorTexto(e)); }
	}, [campusId]);
	// El número de la pestaña WappTeam sale de la lista misma: al abrir una conversación su renglón queda en 0 y el
	// número baja al instante (antes se quedaba con el de la última carga de la lista; Esteban, 2026-10-10: "leí la de
	// IA Washington y sigue diciendo 1").
	const noLeidosLista = canales.reduce((n, c) => n + (c.no_leidos || 0), 0);
	useEffect(() => {
		if (!cargado) return;
		if (onNoLeidos) onNoLeidos(noLeidosLista);
		document.title = (noLeidosLista ? "(" + noLeidosLista + ") " : "") + "WAPP";
	}, [noLeidosLista, cargado]);

	// alrededor: trae de lo más nuevo hasta ese mensaje y lo resalta (buscar). Una recarga normal (mensaje nuevo, palomitas)
	// conserva lo anterior que ya se había cargado (Ver anteriores o un salto) y no jala hasta abajo a quien está leyendo arriba.
	const cargarMensajes = useCallback(async (id: number, antes?: number | null, bajar = true, alrededor?: number | null) => {
		try {
			const r = await http.prospectsService.equipoMensajes(campusId, id, antes, alrededor);
			if (abiertoRef.current !== id) return;
			const lote = r.data.mensajes;
			const prev = mensajesRef.current;
			const desde = !antes && !alrededor && lote.length ? prev.findIndex((m) => m.id === lote[0].id) : -1;
			const caja = zonaChat.current?.querySelector(".eq-mensajes") as HTMLElement | null;
			const abajo = !caja || caja.scrollHeight - caja.scrollTop - caja.clientHeight < 200;
			setCanal(r.data.canal); setVistoHasta(r.data.vistoHasta); setEntregadoHasta(r.data.entregadoHasta ?? null); setFijados(r.data.fijados || []);
			if (antes) {
				// Quedarse en el mismo mensaje que se estaba viendo (Chrome no lo sostiene solo cuando el scroll está en 0).
				const alto = caja ? caja.scrollHeight - caja.scrollTop : 0;
				setHayMas(r.data.hayMas); setMensajes((p) => [...lote, ...p]);
				if (caja) requestAnimationFrame(() => { caja.scrollTop = caja.scrollHeight - alto; });
			}
			else if (desde > 0) setMensajes([...prev.slice(0, desde), ...lote]);   // hayMas sigue como estaba
			else { setHayMas(r.data.hayMas); setMensajes(lote); }
			if (alrededor) setSaltoEq({ id: alrededor, n: Date.now() });
			else if (!antes && bajar && (desde <= 0 || abajo)) setTimeout(() => fin.current?.scrollIntoView({ block: "end" }), 40);
			setCanales((prev) => prev.map((c) => (c.id === id ? { ...c, no_leidos: 0, menciones: 0 } : c)));
		} catch (e) { setError(errorTexto(e)); }
	}, [campusId]);
	const irACita = useSaltoACita(zonaChat, "data-eqm", mensajes, hayMas, () => { if (canal) cargarMensajes(canal.id, mensajes[0]?.id, false); });
	// El salto a un resultado: si el mensaje va dentro de un álbum, se resalta el álbum (los de adentro no se pintan solos).
	useEffect(() => {
		if (!saltoEq) return;
		let id = saltoEq.id;
		const al = agruparAlbumes(mensajesRef.current);
		if (al.dentro.has(id)) al.cabeza.forEach((items, cab) => { if (items.some((x) => x.id === saltoEq.id)) id = cab; });
		irACita(id);
	}, [saltoEq?.n]);
	/** Abrir una conversación en un mensaje (resultado de buscar). */
	const irAMensaje = (canalId: number, msgId: number) => {
		if (abiertoRef.current === canalId) {
			if (canal?.id === canalId && mensajesRef.current.some((m) => m.id === msgId)) setSaltoEq({ id: msgId, n: Date.now() });
			else cargarMensajes(canalId, null, false, msgId);
		} else { saltoPendiente.current = { canal: canalId, id: msgId }; setAbierto(canalId); }
	};
	const hiloDeResultado = useRef<Record<number, number>>({});
	const buscarEnCanal = useCallback(async (q: string): Promise<ItemBusqueda[]> => {
		if (!abierto) return [];
		const r = await http.prospectsService.equipoBuscar(campusId, q, abierto);
		r.data.resultados.forEach((x) => { if (x.hilo_id && !x.hilo_tambien) hiloDeResultado.current[x.id] = x.hilo_id; });
		return r.data.resultados.map((x) => ({ id: x.id, fecha: x.created_at, quien: x.author_name.split(" ")[0], texto: resumenTexto(x.body) || x.file_name || "Archivo" }));
	}, [campusId, abierto]);
	const busquedaGlobal = useBuscarDiferido<EquipoResultado>(buscaLista, 2, String(campusId), (q) => http.prospectsService.equipoBuscar(campusId, q).then((r) => r.data.resultados));

	useEffect(() => { cargarCanales(); return () => { document.title = "WAPP"; }; }, [cargarCanales]);
	useEffect(() => {
		if (!abierto) return;
		setMensajes([]); setRespondiendo(null); setEditando(null); setError(null); canalMenciones.current = abierto; setMenciones(mencionesDe(abierto)); setEscribe(null); setInfo(null);
		setPanel((p) => (p === "favoritos" ? p : null));   // Favoritos sigue a la vista para ir de uno a otro
		setElegidos((prev) => { prev.forEach((e) => e.url && URL.revokeObjectURL(e.url)); return []; }); setErrorArch(null);
		setBuscaCanal(false); setSaltoEq(null); setFijados([]); setFijIdx(0); setFijando(null);
		setHilo(hiloPendiente.current); hiloPendiente.current = null;
		const pend = saltoPendiente.current && saltoPendiente.current.canal === abierto ? saltoPendiente.current.id : null;
		saltoPendiente.current = null;
		mensajesRef.current = [];   // la recarga de la nueva no debe juntarse con los mensajes de la anterior
		cargarMensajes(abierto, null, !pend, pend);
	}, [abierto]);

	// Al instante: mensaje nuevo, editado, reacción, "visto" y "escribiendo…". Respaldo lento si no hay conexión.
	useEffect(() => {
		const offs = [
			push.on("equipo", (d) => {
				const c = Number(d?.c);
				// Primero se lee la conversación abierta (eso la marca leída) y después la lista: al revés, la lista llegaba
				// con el mensaje todavía sin leer y el número se quedaba en 1.
				if (c === abiertoRef.current) cargarMensajes(c, null, !d?.cambio && !d?.h).then(() => cargarCanales());
				else cargarCanales();
				if (Number(d?.h)) {
					if (Number(d.h) === hiloRef.current) setHiloVersion((v) => v + 1);
					cargarHilos();
				}
				// Los avisos (del navegador, en pantalla y sonido) los da wappteamDock para toda la app.
			}),
			push.on("equipo_visto", (d) => {
				const c = Number(d?.c);
				if (c !== abiertoRef.current || !vistoCambiaAlgo(mensajesRef.current, yoId, vistoRef.current)) return;
				clearTimeout(vistoTimer.current);   // las que leen casi a la vez: una sola carga
				vistoTimer.current = setTimeout(() => { if (abiertoRef.current === c) cargarMensajes(c, null, false); }, 1500);
			}),
			push.on("equipo_escribe", (d) => {
				if (Number(d?.c) !== abiertoRef.current) return;
				setEscribe(d?.n || "Alguien");
				clearTimeout(escribeTimer.current);
				escribeTimer.current = setTimeout(() => setEscribe(null), 4000);
			}),
		];
		let t: any;
		const ciclo = () => { t = setTimeout(() => {
			if (document.visibilityState === "visible") { if (abiertoRef.current) cargarMensajes(abiertoRef.current, null, false).then(() => cargarCanales()); else cargarCanales(); }
			ciclo();
		}, Math.max(30000, intervaloRespaldo())); };
		ciclo();
		return () => { offs.forEach((o) => o()); clearTimeout(t); };
	}, [cargarCanales, cargarMensajes, yoId]);

	// El buscador de arriba también encuentra personas (Esteban, 2026-10-13: "el search debería buscar personas"): como
	// WhatsApp, escribes un nombre y le escribes aunque nunca hayan platicado.
	const [personasBusca, setPersonasBusca] = useState<{ id: number; nombre: string; rol: string | null; planteles?: string[] }[]>([]);
	useEffect(() => {
		const q = buscaLista.trim();
		if (q.length < 2) { setPersonasBusca([]); return; }
		const t = setTimeout(() => http.prospectsService.equipoPersonas(campusId, q, null, null, campusId)
			.then((r) => setPersonasBusca(r.data.personas.filter((p) => p.id !== yoId).slice(0, 8))).catch((e) => { setPersonasBusca([]); setError(errorTexto(e)); }), 300);
		return () => clearTimeout(t);
	}, [buscaLista, campusId]);

	// Buscadores de los paneles (personas, explorar, buscar mensajes).
	useEffect(() => {
		if (panel !== "nuevoCanal" && panel !== "nuevoGrupo" && panel !== "directo" && panel !== "miembros" && panel !== "ajustes") return;
		const t = setTimeout(() => http.prospectsService.equipoPersonas(campusId, fBusca, null, fPlantel, campusId).then((r) => { setPersonas(r.data.personas); setMisPlanteles(r.data.planteles || []); }).catch((e) => { setPersonas([]); setError(errorTexto(e)); }), 250);
		return () => clearTimeout(t);
	}, [panel, fBusca, fPlantel]);
	useEffect(() => {
		if (panel !== "explorar") return;
		const t = setTimeout(() => http.prospectsService.equipoExplorar(campusId, fBusca).then((r) => setExplorar(r.data.canales)).catch((e) => { setExplorar([]); setError(errorTexto(e)); }), 250);
		return () => clearTimeout(t);
	}, [panel, fBusca]);
	useEffect(() => {
		if ((panel === "miembros" || panel === "ajustes") && abierto) http.prospectsService.equipoMiembros(campusId, abierto).then((r) => setMiembros(r.data.miembros)).catch((e) => { setMiembros([]); setError(errorTexto(e)); });
	}, [panel, abierto]);

	const abrirPanel = (p: Panel) => { setPanel(p); setMenuNuevo(false); setFBusca(""); setFGente([]); setFNombre(""); setFDesc(""); setFPublico(false); setFAnuncios(false); setError(null); };

	// ---- escribir: menciones con @, "escribiendo…", enviar, editar

	const men = useMenciones(campusId, abierto);
	const alEscribir = (v: string, cursor: number | null) => {
		setTexto(v);
		men.revisar(v, cursor);
		if (abierto && v && Date.now() - ultimoEscribe.current > 3000) {
			ultimoEscribe.current = Date.now();
			http.prospectsService.equipoCanalAccion(campusId, abierto, { accion: "escribiendo" }).catch(() => null);
		}
	};
	const elegirMencion = (p: Persona) => {
		men.poner(caja.current, texto, p, setTexto);
		setMenciones((prev) => (prev.some((x) => x.id === p.id) ? prev : [...prev, { id: p.id, nombre: primerNombre(p) }]));
	};

	// Texto nuevo: sale al instante con relojito (equipoEnCamino) y el cuadro queda libre para el siguiente; los envíos van
	// en fila, así que Enter repetido ya no duplica (el cuadro se vacía al primero).
	const enCamino = useEnCamino(abierto, mensajes, yoId);
	// Editar: un envío a la vez: con el servidor lento, Enter repetido mandaba lo mismo varias veces (12-oct-2026).
	const enviandoRef = useRef(false);
	const enviar = async () => {
		if (!abierto || !texto.trim()) return;
		if (!editando) {
			const canalId = abierto;
			const t = texto.trim();
			const cita = respondiendo;
			const ids = idsMencionados(menciones, texto);
			const ponerTexto = setTextoBorrador;   // el borrador de esta conversación, aunque para cuando falle ya esté abierta otra
			setError(null);
			setTextoBorrador(""); setRespondiendo(null);
			if (canalMenciones.current === canalId) setMenciones([]); else guardarBorrador(yoId, "eqm:" + canalId, "");
			caja.current?.focus({ preventScroll: true });   // el teclado del celular se queda abierto para el siguiente
			setTimeout(() => fin.current?.scrollIntoView({ block: "end" }), 40);
			enCamino.mandar(canalId, t, cita,
				() => http.prospectsService.equipoEnviar(campusId, canalId, t, null, { replyTo: cita?.id || null, menciones: ids }).then((r) => r.data?.id ?? null), {
					ok: () => { if (abiertoRef.current === canalId) cargarMensajes(canalId).then(() => cargarCanales()); else cargarCanales(); },
					// No salió: se quita la burbuja, se avisa y el texto vuelve al cuadro (si no escribió otra cosa).
					mal: (e) => {
						if (abiertoRef.current === canalId) setError(errorTexto(e));
						if (abiertoRef.current === canalId && editandoRef.current) { if (!leerBorrador(yoId, "team:" + canalId).trim()) guardarBorrador(yoId, "team:" + canalId, t); }
						else ponerTexto((antes) => (antes.trim() ? antes : t));
					},
				});
			return;
		}
		if (enviandoRef.current) return;
		enviandoRef.current = true;
		setBusy(true); setError(null);
		try {
			await http.prospectsService.equipoMensajeAccion(campusId, editando.id, { accion: "editar", body: texto.trim() });
			setEditando(null);
			borrador.restaurar();
			setRespondiendo(null);
			await cargarMensajes(abierto);
			cargarCanales();
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); enviandoRef.current = false; }
	};

	// "Enviar más tarde" (Slack): lo que está en el cuadro sale solo a la hora elegida, como yo.
	const programarEnvio = async (iso: string) => {
		setProgramando(false);
		if (!abierto || !texto.trim()) return;
		const t = texto.trim();
		try {
			await http.prospectsService.equipoProgramar(campusId, { canalId: abierto, body: t, menciones: idsMencionados(menciones, t), cuando: iso });
			setTextoBorrador(""); setMenciones([]);
			cargarProgramados(abierto);
			avisoOk("Se manda " + fechaProgramada(iso));
		} catch (e) { setError(errorTexto(e)); }
	};
	const cancelarProgramado = async (id: number, editar: boolean) => {
		try {
			const r = await http.prospectsService.equipoCancelarProgramado(campusId, id);
			if (editar && r.data.body) { setTextoBorrador(r.data.body); caja.current?.focus(); }
			cargarProgramados(abierto);
		} catch (e) { setError(errorTexto(e)); }
	};

	// GIF del buscador: se manda como mensaje con la liga del GIF (WappTeam lo pinta animado).
	const enviarGif = async (url: string) => {
		if (!abierto) return;
		const canalId = abierto;
		setBusy(true); setError(null);
		try {
			await http.prospectsService.equipoEnviar(campusId, canalId, url, null, { replyTo: respondiendo?.id || null, menciones: [] });
			setRespondiendo(null);
			await cargarMensajes(canalId);
			cargarCanales();
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};

	// Sticker de Mis Stickers: el error lo enseña el panel (por eso no se atrapa aquí).
	const enviarSticker = async (s: Sticker) => {
		if (!abierto) return;
		const canalId = abierto;
		setError(null);
		await http.prospectsService.equipoSticker(campusId, canalId, s.id, respondiendo?.id || null);
		setRespondiendo(null);
		await cargarMensajes(canalId);
		cargarCanales();
	};
	// ---- fijar y favoritos
	const cargarFavoritos = useCallback(async (canalId: number | null) => {
		try { const r = await http.prospectsService.equipoFavoritos(campusId, canalId); setFavoritos(r.data.favoritos); }
		catch (e) { setFavoritos([]); setError(errorTexto(e)); }
	}, [campusId]);
	useEffect(() => { if (panel === "favoritos") { setFavoritos(null); cargarFavoritos(favCanal); } }, [panel, favCanal]);
	const verFavoritos = (canalId: number | null) => {
		setFavCanal(canalId); setPanel("favoritos"); setMenuNuevo(false); setError(null);
		// En el celular la lista no se ve con una conversación abierta: se regresa a la lista.
		if (window.matchMedia("(max-width: 700px)").matches) setAbierto(null);
	};
	const fijar = async (id: number, paraTodas: boolean, si: boolean) => {
		setFijando(null); setElegidoMsg(null); setError(null);
		try {
			const r = await http.prospectsService.equipoFijar(campusId, id, paraTodas, si);
			if (abiertoRef.current === canal?.id) { setFijados(r.data.fijados); setFijIdx(0); }
		} catch (e) { setError(errorTexto(e)); }
	};
	const favorito = async (m: EquipoMensaje, si: boolean) => {
		setElegidoMsg(null); setError(null);
		setMensajes((prev) => prev.map((x) => (x.id === m.id ? { ...x, favorito: si } : x)));
		try {
			await http.prospectsService.equipoFavorito(campusId, m.id, si);
			if (panel === "favoritos") cargarFavoritos(favCanal);
		} catch (e) {
			setMensajes((prev) => prev.map((x) => (x.id === m.id ? { ...x, favorito: !si } : x)));
			setError(errorTexto(e));
		}
	};
	const accionMensaje = async (m: EquipoMensaje, accion: "borrar" | "reaccion", emoji?: string) => {
		setReaccionando(null);
		try {
			await http.prospectsService.equipoMensajeAccion(campusId, m.id, { accion, emoji });
			if (abierto) cargarMensajes(abierto, null, false);
		} catch (e) { setError(errorTexto(e)); }
	};

	// ---- varios archivos a la vez: elegir, pegar o arrastrar → bandeja → se mandan en orden (álbum)
	const agregarArchivos = (fs: FileList | File[]) => {
		setErrorArch(null);
		setElegidos((prev) => [...prev, ...elegidosDe(fs)].slice(0, MAX_ARCHIVOS));
	};
	const quitarArchivo = (id: string) => setElegidos((prev) => { const e = prev.find((x) => x.id === id); if (e?.url) URL.revokeObjectURL(e.url); return prev.filter((x) => x.id !== id); });
	const cancelarArchivos = () => { elegidos.forEach((e) => e.url && URL.revokeObjectURL(e.url)); setElegidos([]); setErrorArch(null); setProgresoArch(null); };
	const enviarArchivos = async (comentario: string, mencionados: number[]) => {
		if (!abierto || !elegidos.length) return;
		const canalId = abierto;
		// Las menciones van con el comentario (el primer archivo).
		const err = await enviarVarios(elegidos, comentario, (f, nombre, kind, txt, onProgress) =>
			http.prospectsService.equipoArchivoSubir(campusId, canalId, f, nombre, kind, txt, { replyTo: respondiendo?.id || null, onProgress, menciones: txt ? mencionados : [] }), setProgresoArch);
		setProgresoArch(null);
		if (err) { setErrorArch(err); cargarMensajes(canalId); return; }
		cancelarArchivos(); setRespondiendo(null);
		await cargarMensajes(canalId); cargarCanales();
	};
	const arrastrando = useArchivosEntrantes(zonaChat, !!(abierto && canal?.puedoEscribir), (fs) => agregarArchivos(fs));
	const verInfo = async (m: EquipoMensaje) => {
		setInfo({ id: m.id, datos: null });
		try { const r = await http.prospectsService.equipoInfo(campusId, m.id); setInfo((i) => (i && i.id === m.id ? { id: m.id, datos: r.data } : i)); }
		catch (e) { setInfo(null); setError(errorTexto(e)); }
	};

	// ---- paneles: crear canal o grupo, directo, explorar, miembros, ajustes

	const crear = async (tipo: "canal" | "grupo") => {
		setBusy(true); setError(null);
		try {
			const r = await http.prospectsService.equipoCrear(campusId, tipo === "grupo" ? { tipo, nombre: fNombre.trim() || undefined, userIds: fGente.map((g) => g.id) }
				: { tipo, nombre: fNombre, descripcion: fDesc, visibilidad: fPublico ? "publico" : "privado", anuncios: fAnuncios, userIds: fGente.map((g) => g.id) });
			setPanel(null);
			await cargarCanales();
			setAbierto(r.data.id);
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};
	const directo = async (userId: number) => {
		try {
			const r = await http.prospectsService.equipoDirecto(campusId, userId);
			setPanel(null);
			await cargarCanales();
			setAbierto(r.data.id);
		} catch (e) { setError(errorTexto(e)); }
	};

	const [filtroLista, setFiltroLista] = useState<string>(() => { try { return localStorage.getItem("wappteam.filtro") || "todos"; } catch { return "todos"; } });
	const elegirFiltro = (k: string) => { setFiltroLista(k); try { localStorage.setItem("wappteam.filtro", k); } catch { /* solo esta vez */ } };

	// Acciones del mensaje: menú "Más", recordatorio y marcar no leído; un aviso verde corto cuando algo salió bien.
	const [masMsg, setMasMsg] = useState<number | null>(null);
	const [masArriba, setMasArriba] = useState(false);
	const [recordando, setRecordando] = useState<number | null>(null);
	const [okTexto, setOkTexto] = useState<string | null>(null);
	const okTimer = useRef<any>(null);
	const avisoOk = (t: string) => { setOkTexto(t); clearTimeout(okTimer.current); okTimer.current = setTimeout(() => setOkTexto(null), 3500); };
	const recordar = async (m: EquipoMensaje, iso: string) => {
		setRecordando(null);
		try { await http.prospectsService.equipoRecordar(campusId, m.id, iso); avisoOk("Te lo recuerda IA Washington " + cuando(iso).replace(/^hoy /, "hoy a las ")); }
		catch (e) { setError(errorTexto(e)); }
	};
	const noLeidoDesde = async (m: EquipoMensaje) => {
		try {
			await http.prospectsService.equipoNoLeido(campusId, m.id);
			setAbierto(null);   // como Slack: sales de la conversación y queda en negritas en la lista
			cargarCanales();
		} catch (e) { setError(errorTexto(e)); }
	};

	// Mi estado ("🏫 En clase") y pausar avisos, arriba de la lista.
	const [miEstado, setMiEstado] = useState<EstadoEquipo | null>(null);
	const [estadoAbierto, setEstadoAbierto] = useState(false);
	useEffect(() => {
		http.prospectsService.equipoEstado(campusId).then((r) => {
			setMiEstado(r.data.estado);
			try { if (r.data.estado?.noMolestarHasta) localStorage.setItem("wappteam.pausa", String(Date.parse(r.data.estado.noMolestarHasta.replace(" ", "T") + "Z"))); else localStorage.removeItem("wappteam.pausa"); } catch { /* sin memoria */ }
		}).catch(() => null);
	}, [campusId]);
	useEffect(() => push.on("equipo_estado", () => cargarCanales()), [cargarCanales]);

	// Vistas de la derecha (como Slack): Actividad; y a un lado de la conversación, Archivos y Enlaces.
	const [vistaActividad, setVistaActividad] = useState(false);
	const [adjuntos, setAdjuntos] = useState(false);
	const menTotal = canales.reduce((t, c) => t + (c.menciones || 0), 0);

	// Enviar más tarde, encuesta y lo que tengo programado en esta conversación.
	const [programando, setProgramando] = useState(false);
	const [encuestaNueva, setEncuestaNueva] = useState(false);
	const [programados, setProgramados] = useState<{ id: number; body: string; cuando: string; hilo_id: number | null }[]>([]);
	const [verProgramados, setVerProgramados] = useState(false);
	const cargarProgramados = useCallback(async (canalId: number | null) => {
		if (!canalId) { setProgramados([]); return; }
		try { const r = await http.prospectsService.equipoProgramados(campusId, canalId); setProgramados(r.data.programados); } catch { setProgramados([]); }
	}, [campusId]);
	useEffect(() => { cargarProgramados(abierto); setVerProgramados(false); setAdjuntos(false); }, [abierto, cargarProgramados]);

	// Formato como Slack: barrita "Aa" (se recuerda) y Cmd+B / Cmd+I / Cmd+Shift+X. Marca lo elegido con *, _, ~ o `.
	const [formatoBar, setFormatoBar] = useState(() => { try { return localStorage.getItem("wappteam.formato") === "1"; } catch { return false; } });
	const alternarFormato = () => setFormatoBar((x) => { try { localStorage.setItem("wappteam.formato", x ? "0" : "1"); } catch { /* solo esta vez */ } return !x; });
	const envolver = (marca: string, lista = false) => {
		const el = caja.current;
		if (!el) return;
		const a = el.selectionStart ?? texto.length, b = el.selectionEnd ?? texto.length;
		const sel = texto.slice(a, b);
		let nuevo: string, ini: number, fin2: number;
		if (lista) {
			const bloque = (sel || "").split("\n").map((l) => (l.startsWith("• ") ? l : "• " + l)).join("\n");
			nuevo = texto.slice(0, a) + (a > 0 && texto[a - 1] !== "\n" ? "\n" : "") + bloque + texto.slice(b);
			ini = fin2 = nuevo.length - texto.slice(b).length;
		} else {
			nuevo = texto.slice(0, a) + marca + sel + marca + texto.slice(b);
			ini = a + marca.length; fin2 = ini + sel.length;
		}
		setTexto(nuevo);
		requestAnimationFrame(() => { el.focus(); el.setSelectionRange(ini, fin2); });
	};

	// Leyendo arriba: botón "↓" con cuántos mensajes nuevos llegaron abajo, como Slack.
	const [lejos, setLejos] = useState(false);
	const vistoAbajo = useRef(0);
	const alDesplazar = (e: React.UIEvent<HTMLDivElement>) => {
		const el = e.currentTarget;
		const l = el.scrollHeight - el.scrollTop - el.clientHeight > 300;
		if (l !== lejos) setLejos(l);
		if (!l) vistoAbajo.current = mensajes.length ? mensajes[mensajes.length - 1].id : 0;
	};
	useEffect(() => { setLejos(false); vistoAbajo.current = 0; }, [abierto]);
	useEffect(() => { if (!lejos && mensajes.length) vistoAbajo.current = mensajes[mensajes.length - 1].id; }, [mensajes, lejos]);
	const nuevosAbajo = lejos ? mensajes.filter((m) => m.id > vistoAbajo.current && m.user_id !== yoId).length : 0;

	// Cmd/Ctrl + K: ir a cualquier conversación escribiendo su nombre.
	const [salto, setSalto] = useState(false);
	useEffect(() => {
		const tecla = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSalto((x) => !x); } };
		window.addEventListener("keydown", tecla);
		return () => window.removeEventListener("keydown", tecla);
	}, []);

	/** Abrir un hilo desde la lista de Hilos o un aviso: abre su conversación y el panel. */
	const abrirHilo = (canalId: number, hiloId: number) => {
		setVistaHilos(false);
		setVistaIa(false);
		setVistaActividad(false);
		if (abierto === canalId) setHilo(hiloId);
		else { hiloPendiente.current = hiloId; setAbierto(canalId); }
	};

	// Tarjeta de perfil al tocar una foto, un nombre o una @mención, como Slack y WhatsApp (Esteban, 2026-10-25).
	const [tarjeta, setTarjeta] = useState<AnclaPerfil | null>(null);
	const verPerfil = (userId: number, e: React.MouseEvent) => { e.stopPropagation(); setTarjeta({ userId, x: e.clientX, y: e.clientY }); };
	const tocarMencion = async (e: React.MouseEvent) => {
		const b = (e.target as HTMLElement).closest(".eq-mencion");
		const fila = b?.closest("[data-eqm]");
		if (!b || !fila || !canal) return;
		e.stopPropagation();
		const m = mensajes.find((x) => x.id === Number(fila.getAttribute("data-eqm")));
		const nombre = (b.textContent || "").replace(/^@/, "").trim();
		const punto = { x: e.clientX, y: e.clientY };
		try {
			const r = await http.prospectsService.equipoPersonas(campusId, nombre, canal.id);
			const p = r.data.personas.find((x) => m?.menciones.includes(x.id)) || r.data.personas[0];
			if (p) setTarjeta({ userId: p.id, ...punto });
		} catch { /* sin tarjeta: la mención sigue siendo texto */ }
	};

	// Esc cierra Info del mensaje y el panel de Personas / Ajustes (barrido 26-oct).
	useEffect(() => {
		const esc = (e: KeyboardEvent) => {
			if (e.key !== "Escape") return;
			if (info) { setInfo(null); return; }
			if (panel === "miembros" || panel === "ajustes") setPanel(null);
		};
		window.addEventListener("keydown", esc);
		return () => window.removeEventListener("keydown", esc);
	}, [info, panel]);

	// La conversación abierta siempre a la vista en la lista (un canal nuevo sin mensajes queda hasta abajo; barrido 26-oct).
	useEffect(() => {
		if (!abierto) return;
		const t = setTimeout(() => document.querySelector(".eq-lista .eq-fila.activa")?.scrollIntoView({ block: "nearest" }), 120);
		return () => clearTimeout(t);
	}, [abierto, canales.length]);

	// Línea "Nuevos" sobre el primer mensaje sin leer, como Slack: se toma al abrir la conversación.
	const [nuevos, setNuevos] = useState<{ canal: number; n: number } | null>(null);
	const canalesRef = useRef(canales);
	canalesRef.current = canales;
	useEffect(() => {
		const c = canalesRef.current.find((x) => x.id === abierto);
		setNuevos(abierto && c && c.no_leidos ? { canal: abierto, n: c.no_leidos } : null);
	}, [abierto]);
	const accionCanal = async (data: any, luego?: () => void) => {
		if (!abierto) return;
		setError(null);
		try {
			await http.prospectsService.equipoCanalAccion(campusId, abierto, data);
			if (luego) luego();
			await cargarCanales();
			if (abiertoRef.current) cargarMensajes(abiertoRef.current, null, false);
			if (panel === "miembros" || panel === "ajustes") http.prospectsService.equipoMiembros(campusId, abierto).then((r) => setMiembros(r.data.miembros)).catch(() => null);
		} catch (e) { setError(errorTexto(e)); }
	};
	const unirse = async (id: number) => {
		try {
			await http.prospectsService.equipoCanalAccion(campusId, id, { accion: "unirse" });
			setPanel(null); await cargarCanales(); setAbierto(id);
		} catch (e) { setError(errorTexto(e)); }
	};
	useEffect(() => { http.prospectsService.equipoSupervision(campusId).then((r) => setSupGente(r.data.personas)).catch(() => setSupGente([])); }, [campusId]);
	useEffect(() => {
		if (!supPersona) { setSupCanales([]); return; }
		http.prospectsService.equipoSupervision(campusId, supPersona).then((r) => setSupCanales(r.data.canales || [])).catch((e) => setError(errorTexto(e)));
	}, [supPersona]);
	// Activa también los avisos con WAPP cerrada (push), no solo los de la pestaña abierta.
	const pedirAvisos = () => {
		activarAvisos().catch(() => null).finally(() => setAvisos(typeof Notification !== "undefined" ? Notification.permission : "denied"));
	};

	const toggleGente = (p: { id: number; nombre: string }) => setFGente((prev) => (prev.some((x) => x.id === p.id) ? prev.filter((x) => x.id !== p.id) : [...prev, p]));
	const listaPersonas = (onPick: (p: { id: number; nombre: string }) => void, marcadas?: number[], enfocar = true) => (
		<>
			<div className="eq-gente-busca">
				<input type="search" autoFocus={enfocar} placeholder="Nombre o rol" value={fBusca} onChange={(e) => setFBusca(e.target.value)} aria-label="Buscar por nombre o rol" />
				{misPlanteles.length > 1 ? <PlantelSelect opciones={misPlanteles} value={fPlantel} onChange={setFPlantel} /> : null}
			</div>
			{/* Sin lista gigante (Esteban, 2026-10-12): resultados solo al escribir; primero la gente de este plantel. */}
			{fBusca.trim().length < 2 ? <div className="eq-gente-pista">Escribe un nombre o un rol, por ejemplo "Asesora de Ventas" o "Supervisión".</div>
			: !personas.length ? <div className="eq-gente-pista">Nadie coincide con "{fBusca.trim()}".</div> : null}
			<div className="eq-personas">
				{(fBusca.trim().length < 2 ? [] : personas).map((p) => (
					<button key={p.id} className={"eq-persona" + (marcadas?.includes(p.id) ? " on" : "")} onClick={() => onPick(p)}>
						<b>{marcadas?.includes(p.id) ? "✓ " : ""}{p.nombre}</b>{p.rol ? <span>{p.rol}{p.planteles?.length ? " · " + p.planteles.join(", ") : ""}</span> : null}
					</button>
				))}
			</div>
		</>
	);

	const icono = (c: { id: number; tipo: string; anuncios: boolean; visibilidad: string; automatico: boolean; icono?: string | null }): React.ReactNode =>
		c.icono && c.tipo !== "directo" ? <IconoCanal id={c.id} icono={c.icono} />
		: c.tipo === "canal" ? (c.anuncios ? <WappIcono n="megaphone" /> : c.visibilidad === "privado" && !c.automatico ? <WappIcono n="lock" /> : "#") : null;
	const fila = (c: EquipoCanal) => {
		const [autor, ftipo, txt] = (c.ultimo || "").split("|");
		const prev = c.ultimo ? (autor ? autor.split(" ")[0] + ": " : "") + (resumenTexto(txt) || (ftipo === "audio" ? "Nota de voz" : ftipo === "image" ? "Foto" : ftipo === "sticker" ? "Sticker" : ftipo ? "Archivo" : "Cliente compartido")) : (c.descripcion || "Sin mensajes");
		return (
			<button key={c.id} className={"ventas-fila eq-fila" + (abierto === c.id ? " activa" : "") + (c.silenciado ? " silenciado" : "") + (c.fijo || c.fijada || c.anuncioFijo ? " fija" : "")} onClick={() => setAbierto(c.id)}>
				<span className={"av " + (c.tipo === "canal" ? "eq-canal" : c.tipo === "grupo" ? "eq-grupo-av" : tonoAvatar(c.id)) + (c.otro_id && fotos[c.otro_id] ? " con-foto" : "") + (c.icono && c.tipo !== "directo" ? " eq-con-emoji" : "")} aria-hidden="true">
					{c.otro_id && fotos[c.otro_id] ? <img src={fotos[c.otro_id]} alt="" loading="lazy" />
						: icono(c) || (c.tipo === "grupo" ? <WappIcono n="users" /> : iniciales({ first_name: c.nombre.split(" ")[0], last_name: c.nombre.split(" ")[1] || "" }))}
					{c.tipo === "directo" && c.enLinea ? <i className="ex-punto" title="Activa ahora" /> : null}</span>
				<span className="cuerpo">
					<span className="l1"><span className="nombre">{c.nombre}</span>
						{c.estado?.emoji ? <span className="ex-fila-estado" title={(c.estado.texto || "") + (c.estado.hasta ? " · " + hastaTexto(c.estado.hasta) : "")}>{c.estado.emoji}</span> : null}
						{c.fijo || c.fijada || c.anuncioFijo ? <span className="eq-fijo" title="Fija arriba" aria-label="Fija arriba"><WappIcono n="fijar" /></span> : null}
						<span className={"cuando" + (c.no_leidos ? " nuevo" : "")}>{c.last_message_at ? cuando(c.last_message_at) : ""}</span></span>
					<span className="l2"><span className="txt">{prev}</span>
						{c.silenciado ? <span className="eq-mute" title="Silenciado" aria-label="Silenciado"><WappIcono n="bellOff" /></span> : null}
						{c.menciones ? <span className="badge-n mencion">@</span> : null}
						{c.no_leidos ? <span className="badge-n">{c.no_leidos}</span> : null}</span>
				</span>
			</button>
		);
	};

	// Filtros arriba de la lista, como WhatsApp (Esteban, 2026-10-25: "tipo WhatsApp + Canales + Grupos"). Se recuerda el último.
	const FILTROS: { k: string; t: string; f: (c: EquipoCanal) => boolean }[] = [
		{ k: "todos", t: "Todos", f: () => true },
		{ k: "noleidos", t: "No Leídos", f: (c) => !!c.no_leidos || !!c.menciones },
		{ k: "menciones", t: "Menciones", f: (c) => !!c.menciones },
		{ k: "canales", t: "Canales", f: (c) => c.tipo === "canal" },
		{ k: "grupos", t: "Grupos", f: (c) => c.tipo === "grupo" },
		{ k: "directos", t: "Directos", f: (c) => c.tipo === "directo" },
	];
	const filtroAct = FILTROS.find((x) => x.k === filtroLista) || FILTROS[0];
	const cuentaFiltro = (k: string) => (k === "noleidos" ? canales.filter((c) => !c.silenciado && (c.no_leidos || c.menciones)).length : k === "menciones" ? canales.filter((c) => c.menciones).length : 0);
	// La conversación abierta no desaparece de la lista aunque ya no cumpla el filtro (al leerla deja de ser "no leída").
	const listaFiltrada = canales.filter((c) => filtroAct.f(c) || c.id === abierto);

	// Ícono: en canales lo cambian las administradoras (y Super Admin); en grupos cualquiera del grupo.
	const puedeIcono = !!canal && !canal.supervision && (canal.tipo === "grupo" || (canal.tipo === "canal" && canal.soyAdmin));
	const ultimoMio = [...mensajes].reverse().find((m) => m.user_id === yoId && !m.deleted_at);
	const primerNuevo = (() => {
		if (!nuevos || nuevos.canal !== abierto) return null;
		const ajenos = enCamino.vista.filter((m) => m.user_id !== yoId && !m.enCamino);
		return ajenos.length >= nuevos.n ? ajenos[ajenos.length - nuevos.n].id : null;
	})();
	let ultimoDia = -1;
	let ultimoAutor: number | null = null;
	let ultimoTs = 0;
	let albumCabeza = new Map<number, EquipoMensaje[]>();
	let albumDentro = new Set<number>();

	return (
		<div className={"ventas" + (abierto || vistaIa ? " con-cliente" : "")}>
			<div className="ventas-cuerpo">
				<div className="ventas-lista eq-lista">
					<div className="eq-cab">
						<b>WappTeam</b>
						<div className="ex-estado-ancla">
							<button type="button" className={"ex-estado-chip" + (miEstado?.noMolestarHasta ? " pausa" : "")} onClick={() => setEstadoAbierto(!estadoAbierto)}
								title={miEstado?.texto ? miEstado.texto + (miEstado.hasta ? " · " + hastaTexto(miEstado.hasta) : "") : miEstado?.noMolestarHasta ? "Avisos pausados " + hastaTexto(miEstado.noMolestarHasta) : "Actualizar tu estado"}
								aria-label="Tu estado">
								{miEstado?.noMolestarHasta ? <WappIcono n="bellOff" /> : miEstado?.emoji ? <span aria-hidden="true">{miEstado.emoji}</span> : <WappIcono n="smile" />}
							</button>
							{estadoAbierto ? <EstadoMenu campusId={campusId} estado={miEstado} onCambio={setMiEstado} onCerrar={() => setEstadoAbierto(false)} /> : null}
						</div>
						<button className="eq-ico" title="Explorar canales" aria-label="Explorar canales públicos" onClick={() => abrirPanel(panel === "explorar" ? null : "explorar")}><WappIcono n="compass" /></button>
						<button className={"eq-ico" + (panel === "favoritos" ? " on" : "")} title="Favoritos" aria-label="Mensajes favoritos" aria-pressed={panel === "favoritos"}
							onClick={() => (panel === "favoritos" ? setPanel(null) : verFavoritos(null))}><WappIcono n="star" /></button>
						{supGente.length ? <button className={"eq-ico" + (panel === "supervision" ? " on" : "")} title="Leer conversaciones del personal (solo Super Admin)" aria-label="Conversaciones del personal" onClick={() => abrirPanel(panel === "supervision" ? null : "supervision")}><WappIcono n="users" /></button> : null}
						<div className="cv-menu">
							<button className="btn-wa pri eq-nuevo-btn" aria-haspopup="menu" aria-expanded={menuNuevo} onClick={() => setMenuNuevo(!menuNuevo)}>+ Nuevo</button>
							{menuNuevo ? (
								<div className="cv-menu-lista eq-menu-nuevo" role="menu">
									<button role="menuitem" onClick={() => abrirPanel("directo")}><WappIcono n="user" />Mensaje directo</button>
									<button role="menuitem" onClick={() => abrirPanel("nuevoGrupo")}><WappIcono n="users" />Grupo</button>
									<button role="menuitem" onClick={() => abrirPanel("nuevoCanal")}><WappIcono n="hash" />Canal</button>
								</div>
							) : null}
						</div>
					</div>
					{avisos === "default" ? <button className="eq-avisos" onClick={pedirAvisos}>Activar Avisos</button> : null}
					{error && !abierto ? <div className="aviso error" style={{ margin: 10 }}>{error}</div> : null}

					{panel === "supervision" ? (
						<div className="eq-panel"><h4>Conversaciones del personal</h4>
							<select value={supPersona || ""} onChange={(e) => setSupPersona(e.target.value ? Number(e.target.value) : null)} aria-label="Persona">
								<option value="">Elige a una persona…</option>
								{supGente.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
							</select>
							<div className="eq-personas">
								{supCanales.map((c) => (
									<button key={c.id} className={"eq-persona" + (abierto === c.id ? " on" : "")} onClick={() => setAbierto(c.id)}>
										<b>{c.tipo === "canal" ? "# " : ""}{c.nombre}</b><span>{cuando(c.last_message_at)}</span>
									</button>
								))}
								{supPersona && !supCanales.length ? <span className="apagado">No tiene conversaciones.</span> : null}
							</div>
						</div>
					) : null}
					{panel === "directo" ? <div className="eq-panel"><h4>Mensaje directo</h4>{listaPersonas((p) => directo(p.id))}</div> : null}
					{panel === "nuevoGrupo" ? (
						<div className="eq-panel"><h4>Grupo nuevo</h4>
							<input value={fNombre} onChange={(e) => setFNombre(e.target.value)} placeholder="Nombre del grupo (opcional)" aria-label="Nombre del grupo" maxLength={120} />
							{fGente.length ? <div className="eq-chips">{fGente.map((g) => <span key={g.id} className="chip">{g.nombre} <button onClick={() => toggleGente(g)} aria-label={"Quitar a " + g.nombre}>×</button></span>)}</div> : null}
							{listaPersonas(toggleGente, fGente.map((g) => g.id))}
							<div className="fila-botones"><button className="btn-wa pri" disabled={busy || fGente.length < 2} onClick={() => crear("grupo")}>Crear grupo</button>
								<button className="btn-wa" onClick={() => setPanel(null)}>Cancelar</button></div>
						</div>
					) : null}
					{panel === "nuevoCanal" ? (
						<div className="eq-panel"><h4>Canal nuevo</h4>
							<input value={fNombre} onChange={(e) => setFNombre(e.target.value)} placeholder="Nombre del canal" aria-label="Nombre del canal" autoFocus />
							<input value={fDesc} onChange={(e) => setFDesc(e.target.value)} placeholder="Para qué es (opcional)" aria-label="Descripción" />
							<label className="eq-check"><input type="checkbox" checked={fPublico} onChange={(e) => setFPublico(e.target.checked)} /> Público: cualquiera lo encuentra y entra</label>
							<label className="eq-check"><input type="checkbox" checked={fAnuncios} onChange={(e) => setFAnuncios(e.target.checked)} /> De anuncios: solo las administradoras escriben</label>
							{fGente.length ? <div className="eq-chips">{fGente.map((g) => <span key={g.id} className="chip">{g.nombre} <button onClick={() => toggleGente(g)} aria-label={"Quitar a " + g.nombre}>×</button></span>)}</div> : null}
							{listaPersonas(toggleGente, fGente.map((g) => g.id))}
							<div className="fila-botones"><button className="btn-wa pri" disabled={busy || fNombre.trim().length < 2} onClick={() => crear("canal")}>Crear canal</button>
								<button className="btn-wa" onClick={() => setPanel(null)}>Cancelar</button></div>
						</div>
					) : null}
					{panel === "explorar" ? (
						<div className="eq-panel"><h4>Canales públicos</h4>
							<input type="search" autoFocus placeholder="Buscar canal" value={fBusca} onChange={(e) => setFBusca(e.target.value)} aria-label="Buscar canal" />
							{explorar.map((c) => (
								<div key={c.id} className="eq-explorar"><span><b>{c.anuncios ? <WappIcono n="megaphone" /> : "# "} {c.nombre}</b><small>{c.descripcion || ""} {c.miembros} personas</small></span>
									<button className="btn-wa" onClick={() => unirse(c.id)}>Unirme</button></div>
							))}
							{!explorar.length ? <span className="apagado">No hay canales públicos nuevos.</span> : null}
						</div>
					) : null}
					{/* Buscar a la vista como WhatsApp: filtra las conversaciones por nombre y, debajo, los mensajes que lo dicen. */}
					<div className="bm-lista-busca">
						<svg className="bm-lupa" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.2" /><path d="M15.5 15.5 21 21" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
						<input type="search" enterKeyHint="search" placeholder="Buscar persona o mensaje" value={buscaLista} onChange={(e) => setBuscaLista(e.target.value)}
							onKeyDown={(e) => { if (e.key === "Escape") setBuscaLista(""); }} aria-label="Buscar persona, conversación o mensaje" />
						{buscaLista ? <button className="bm-ico" onClick={() => setBuscaLista("")} aria-label="Borrar búsqueda">
							<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg></button> : null}
					</div>

					{!buscaLista.trim() && panel !== "favoritos" ? (
						<div className="eq-filtros" role="tablist" aria-label="Filtrar conversaciones">
							{FILTROS.map((x) => {
								const n = cuentaFiltro(x.k);
								return <button key={x.k} type="button" role="tab" aria-selected={filtroAct.k === x.k} className={"eq-filtro" + (filtroAct.k === x.k ? " on" : "")} onClick={() => elegirFiltro(x.k)}>
									{x.t}{n ? <span className="n">{n}</span> : null}</button>;
							})}
						</div>
					) : null}
					<div className="filas">
						{!buscaLista.trim() && panel !== "favoritos" ? (
							<button type="button" className={"eq-atajo" + (vistaHilos && !abierto ? " activa" : "")} onClick={() => { setAbierto(null); setVistaIa(false); setVistaActividad(false); setVistaHilos(true); cargarHilos(); }}>
								<span className="eq-atajo-ico" aria-hidden="true"><WappIcono n="message" /></span><span className="eq-atajo-txt">Hilos</span>
								{hilosTotal ? <span className="badge-n">{hilosTotal}</span> : null}
							</button>
						) : null}
						{!buscaLista.trim() && panel !== "favoritos" ? (
							<button type="button" className={"eq-atajo" + (vistaActividad && !abierto ? " activa" : "")} onClick={() => { setAbierto(null); setVistaIa(false); setVistaHilos(false); setVistaActividad(true); }}>
								<span className="eq-atajo-ico" aria-hidden="true"><WappIcono n="bell" /></span><span className="eq-atajo-txt">Actividad</span>
								{menTotal ? <span className="badge-n mencion">@{menTotal > 1 ? " " + menTotal : ""}</span> : null}
							</button>
						) : null}
						{/* Una sola lista, lo más reciente arriba, como Cliq y WhatsApp (Esteban, 2026-10-10): canales, grupos y directos
						    mezclados en el orden que manda la API (último mensaje primero). IA Washington va fija hasta arriba para
						    todas; lo que IA manda a todo el personal (Super Admin) se abre desde su conversación. */}
						{panel === "favoritos" ? (
							<div className="eq-favs">
								<div className="bm-titulo eq-favs-tit"><WappIcono n="star" className="eq-fav-ico" />
									<span className="eq-favs-nombre">{favCanal ? "Favoritos de " + ((canales.find((c) => c.id === favCanal)?.nombre) || "esta conversación") : "Favoritos"}</span>
									{favCanal ? <button className="enlace" onClick={() => setFavCanal(null)}>Ver Todos</button> : null}
									<button className="bm-ico eq-favs-x" onClick={() => setPanel(null)} aria-label="Cerrar Favoritos" title="Cerrar"><WappIcono n="x" /></button></div>
								{favoritos === null ? <div className="ventas-vacio">Cargando…</div>
								: !favoritos.length ? <div className="bm-vacio">Toca la estrella de un mensaje para guardarlo aquí.</div>
								: favoritos.map((r) => {
									const nombre = canales.find((c) => c.id === r.canal_id)?.nombre || r.nombre || "Conversación";
									return (
										<button key={r.id} className={"ventas-fila bm-fila eq-fav-fila" + (abierto === r.canal_id ? " activa" : "")} onClick={() => (r.hilo_id && !r.hilo_tambien ? abrirHilo(r.canal_id, r.hilo_id) : irAMensaje(r.canal_id, r.id))}>
											<span className="cuerpo">
												<span className="l1"><span className="nombre">{(r.tipo === "canal" ? "# " : "") + nombre}</span><span className="cuando">{cuando(r.created_at)}</span></span>
												<span className="l2"><span className="txt bm-txt">{r.author_name.split(" ")[0]}: {textoCorto(r.body, r.file_tipo, r.file_name, r.prospect_id)}</span></span>
											</span>
										</button>
									);
								})}
							</div>
						) : buscaLista.trim() ? (() => {
							const t = sinAcentos(buscaLista.trim());
							const conv = canales.filter((c) => sinAcentos(c.nombre || "").includes(t));
							const res = busquedaGlobal.resultados;
							return (
								<>
									{conv.length ? <div className="bm-titulo">Conversaciones</div> : null}
									{conv.map(fila)}
									{(() => {
										// Personas con las que todavía no hay directo a la vista (las que sí, ya salen arriba).
										const ya = new Set(conv.filter((c) => c.tipo === "directo").map((c) => c.otro_id));
										const gente = personasBusca.filter((p) => !ya.has(p.id));
										return gente.length ? (
											<>
												<div className="bm-titulo">Personas</div>
												{gente.map((p) => (
													<button key={"p" + p.id} className="ventas-fila eq-fila" onClick={() => { setBuscaLista(""); directo(p.id); }}>
														<span className={"av " + tonoAvatar(p.id) + (fotos[p.id] ? " con-foto" : "")} aria-hidden="true">
															{fotos[p.id] ? <img src={fotos[p.id]} alt="" loading="lazy" /> : iniciales({ first_name: p.nombre.split(" ")[0], last_name: p.nombre.split(" ")[1] || "" })}</span>
														<span className="cuerpo">
															<span className="l1"><span className="nombre">{p.nombre}</span><span className="cuando">Escribirle</span></span>
															<span className="l2"><span className="txt">{[p.rol, (p.planteles || []).join(", ")].filter(Boolean).join(" · ")}</span></span>
														</span>
													</button>
												))}
											</>
										) : null;
									})()}
									{buscaLista.trim().length >= 2 ? (
										<div className="bm-lista-msgs">
											<div className="bm-titulo">Mensajes{busquedaGlobal.buscando ? <span className="wl-cargando" aria-label="Buscando" /> : null}</div>
											{res && !res.length && !busquedaGlobal.buscando ? <div className="bm-vacio">Ningún mensaje dice «{buscaLista.trim()}».</div> : null}
											{(res || []).map((r) => {
												const nombre = canales.find((c) => c.id === r.canal_id)?.nombre || r.nombre || "Conversación";
												return (
													<button key={r.id} className={"ventas-fila bm-fila" + (abierto === r.canal_id ? " activa" : "")} onClick={() => (r.hilo_id && !r.hilo_tambien ? abrirHilo(r.canal_id, r.hilo_id) : irAMensaje(r.canal_id, r.id))}>
														<span className="cuerpo">
															<span className="l1"><span className="nombre">{(r.tipo === "canal" ? "# " : "") + nombre}</span><span className="cuando">{cuando(r.created_at)}</span></span>
															<span className="l2"><span className="txt bm-txt">{r.author_name.split(" ")[0]}: <Resaltado texto={resumenTexto(r.body) || r.file_name || "Archivo"} q={buscaLista} /></span></span>
														</span>
													</button>
												);
											})}
										</div>
									) : null}
								</>
							);
						})() : (
							<>
								{listaFiltrada.map(fila)}
								{cargado && !listaFiltrada.length ? <div className="bm-vacio">{filtroAct.k === "noleidos" ? "Estás al día: nada sin leer." : filtroAct.k === "menciones" ? "Nadie te ha mencionado." : "No hay " + filtroAct.t.toLowerCase() + "."}</div> : null}
							</>
						)}
						{!cargado ? <div className="ventas-vacio">Cargando…</div> : null}
					</div>
				</div>

				{vistaActividad && !abierto ? <ActividadVista campusId={campusId} onCerrar={() => setVistaActividad(false)} onPerfil={verPerfil}
					onIr={(c, id, h) => { setVistaActividad(false); if (h) abrirHilo(c, h); else irAMensaje(c, id); }} />
				: vistaHilos && !abierto ? <HilosVista hilos={hilos} onCerrar={() => setVistaHilos(false)} onAbrir={(c, h) => abrirHilo(c, h)} onPerfil={verPerfil} />
				: vistaIa && !abierto ? <AvisosIaView campusId={campusId} onCerrar={() => { setVistaIa(false); setAbierto(canales.find((c) => c.fijo)?.id ?? null); }} onAbrir={(id) => { setVistaIa(false); setAbierto(id); }} />
				: abierto && canal ? (
					<div className={"ventas-cliente cv eq-chat" + (arrastrando ? " arrastrando" : "")} ref={zonaChat}>
						{arrastrando ? <div className="eqm-soltar"><WappIcono n="upload" /> Suelta aquí para mandar</div> : null}
						<div className="cli-cab wchat-cab">
							<div className="cab-fila">
								<button className="cv-volver" onClick={() => setAbierto(null)} aria-label="Volver a WappTeam">← <span className="txt">WAPPTEAM</span></button>
								<div className="quien">
									{canal.tipo !== "directo" ? (puedeIcono
										? <button type="button" className={"eq-cab-av puede" + (canal.icono ? " eq-con-emoji" : canal.tipo === "grupo" ? " grupo" : "")} onClick={() => setEligiendoIcono(true)}
											title="Cambiar Ícono" aria-label="Cambiar Ícono">{icono(canal) || <WappIcono n="users" />}</button>
										: <span className={"eq-cab-av" + (canal.icono ? " eq-con-emoji" : canal.tipo === "grupo" ? " grupo" : "")} aria-hidden="true">{icono(canal) || <WappIcono n="users" />}</span>) : null}
									{canal.tipo === "directo" ? (() => {
										const otro = canales.find((c) => c.id === canal.id);
										const n = otro?.nombre || canal.nombre || "";
										return <button type="button" className={"av eq-av-cab " + tonoAvatar(canal.id) + (otro?.otro_id && fotos[otro.otro_id] ? " con-foto" : "")}
											onClick={otro?.otro_id ? (e) => verPerfil(otro.otro_id!, e) : undefined} aria-label={"Perfil de " + n}>
											{otro?.otro_id && fotos[otro.otro_id] ? <img src={fotos[otro.otro_id]} alt="" /> : iniciales({ first_name: n.split(" ")[0], last_name: n.split(" ")[1] || "" })}</button>;
									})() : null}
									{(() => {
										const otroId = canal.tipo === "directo" ? canales.find((c) => c.id === canal.id)?.otro_id : null;
										const n = canal.tipo === "canal" ? canal.nombre : canales.find((c) => c.id === canal.id)?.nombre || canal.nombre;
										return otroId ? <button type="button" className="nombre eq-nombre-cab" onClick={(e) => verPerfil(otroId, e)} title="Ver perfil">{n}</button>
											: <span className="nombre">{n}</span>;
									})()}
									{canal.silenciado ? <span className="eq-mute" title="Silenciado" aria-label="Silenciado"><WappIcono n="bellOff" /></span> : null}
									{canal.tipo !== "directo" && !canal.miembrosOculto ? <button className="enlace tel eq-personas" onClick={() => setPanel(panel === "miembros" ? null : "miembros")}
										title="Ver Personas" aria-label={canal.miembros + " personas"}><WappIcono n="users" /><span>{canal.miembros}<span className="txt"> personas</span></span></button> : null}
									{canal.descripcion ? <span className="origen">{canal.descripcion}</span> : null}
									{canal.tipo === "directo" ? (() => {
										const o = canales.find((c) => c.id === canal.id);
										const e = o?.estado;
										return e?.emoji || e?.texto || o?.enLinea ? <span className="origen ex-cab-estado">{o?.enLinea ? <i className="ex-punto suelto" /> : null}{e?.emoji ? e.emoji + " " : ""}{e?.texto || (o?.enLinea ? "Activa ahora" : "")}{e?.hasta ? " · " + hastaTexto(e.hasta) : ""}</span> : null;
									})() : null}
								</div>
								{/* Mismos botones y forma que WappChat (Esteban, 2026-10-13: "quedó diferente y desalineado"): Llamar naranja,
								    buscar y ⋮ con lo demás (silenciar, personas, ajustes, salir, avisos). */}
								<div className="cli-acc">
									{canal.ia ? <button className="btn-wa solo-pc" onClick={() => navigate("/avisos")} title="Todos tus avisos y por dónde te llegan">Mis Avisos</button> : null}
									{!canal.ia && !canal.anuncios && !canal.supervision ? <BotonLlamar campusId={campusId} canalId={canal.id} className="btn-wa cv-b llamar cv-ico" conTexto onError={setError} /> : null}
									{canal.tipo === "canal" && canal.soyAdmin ? <button className={"btn-wa solo-pc" + (panel === "ajustes" ? " on" : "")} onClick={() => setPanel(panel === "ajustes" ? null : "ajustes")}>Ajustes</button> : null}
									<button className={"btn-wa cv-ico cv-buscar-btn" + (buscaCanal ? " on" : "")} aria-pressed={buscaCanal} title="Buscar en la conversación" aria-label="Buscar en la conversación"
										onClick={() => setBuscaCanal(!buscaCanal)}>
										<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.2" /><path d="M15.5 15.5 21 21" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
									</button>
									<div className="cv-menu">
										<button className="btn-wa cv-ico cv-puntos" aria-haspopup="menu" aria-expanded={masCab} onClick={() => setMasCab(!masCab)} aria-label="Más opciones">⋮</button>
										{masCab ? <div className="wl-velo" onClick={() => setMasCab(false)} aria-hidden="true" /> : null}
										{masCab ? (
											<div className="cv-menu-lista" role="menu" onClick={() => setMasCab(false)}>
												{canal.ia ? <button role="menuitem" className="solo-movil" onClick={() => navigate("/avisos")}>Mis Avisos</button> : null}
												{canal.ia && esSuper ? <button role="menuitem" onClick={() => { setAbierto(null); setVistaIa(true); }}>Ver Todo lo que Manda IA</button> : null}
												{canal.tipo !== "directo" && !canal.miembrosOculto ? <button role="menuitem" onClick={() => setPanel("miembros")}>Ver Personas ({canal.miembros})</button> : null}
												{canal.tipo === "canal" && canal.soyAdmin ? <button role="menuitem" className="solo-movil" onClick={() => setPanel("ajustes")}>Ajustes</button> : null}
												{puedeIcono ? <button role="menuitem" onClick={() => setEligiendoIcono(true)}>Cambiar Ícono</button> : null}
												{canal.tipo === "grupo" && !canal.supervision ? <button role="menuitem" onClick={() => {
													const n = window.prompt("Nombre del grupo (vacío = los nombres de quienes están)", canales.find((c) => c.id === canal.id)?.nombre || "");
													if (n !== null) accionCanal({ accion: "editar", nombre: n.trim() });
												}}>Cambiar Nombre</button> : null}
												{!canal.supervision && !canal.ia ? (() => {
													const fija = !!canales.find((c) => c.id === canal.id)?.fijada;
													return <button role="menuitem" onClick={() => accionCanal({ accion: "fijarConversacion", si: !fija })}>{fija ? "Desfijar de la Lista" : "Fijar en la Lista"}</button>;
												})() : null}
												{!canal.supervision ? <button role="menuitem" onClick={() => verFavoritos(canal.id)}>Ver Favoritos</button> : null}
												<button role="menuitem" onClick={() => { setHilo(null); setPanel(null); setAdjuntos(true); }}>Archivos y Enlaces</button>
												<button role="menuitem" onClick={() => accionCanal({ accion: "silenciar", si: !canal.silenciado })}>{canal.silenciado ? "Quitar Silencio" : "Silenciar"}</button>
												{canal.tipo !== "directo" && !canal.automatico ? <button role="menuitem" className="peligro" onClick={() => {
													const que = canal.tipo === "grupo" ? "grupo" : "canal";
													if (window.confirm("¿Salir del " + que + " «" + canal.nombre + "»?" + (canal.tipo === "grupo" || canal.visibilidad === "privado" ? " Para volver alguien te tiene que agregar." : "")))
														accionCanal({ accion: "salir" }, () => setAbierto(null));
												}}>{canal.tipo === "grupo" ? "Salir del Grupo" : "Salir del Canal"}</button> : null}
											</div>
										) : null}
									</div>
								</div>
							</div>
						</div>

						{canal.tipo !== "directo" ? <LlamadaViva campusId={campusId} llamada={canal.llamada} onError={setError} /> : null}
						{buscaCanal ? <BuscadorChat llave={String(canal.id)} pedir={buscarEnCanal} onIr={(id) => (hiloDeResultado.current[id] ? setHilo(hiloDeResultado.current[id]) : irAMensaje(canal.id, id))} onCerrar={() => setBuscaCanal(false)} /> : null}
						{fijados.length ? (() => {
							const i = fijIdx % fijados.length;
							const f = fijados[i];
							const puedeSoltar = !canal.supervision && (f.paraTodas ? !!canal.puedoFijarTodas : f.mio);
							const para = f.paraTodas ? (canal.tipo === "directo" ? "Fijado para los dos" : "Fijado para todas" + (f.por ? " por " + f.por : "")) : "Fijado solo para ti";
							return (
								<div className="eq-fijados">
									{fijados.length > 1 ? <span className="eq-fij-marcas" aria-hidden="true">{fijados.map((x, k) => <i key={x.id + (x.paraTodas ? "t" : "m")} className={k === i ? "on" : ""} />)}</span> : null}
									<button className="eq-fij-txt" title="Ir al mensaje" onClick={() => { irAMensaje(canal.id, f.id); setFijIdx((i + 1) % fijados.length); }}>
										<WappIcono n="fijar" className="eq-fij-ico" />
										<span className="eq-fij-lineas"><small>{para}{fijados.length > 1 ? " · " + (i + 1) + " de " + fijados.length : ""}</small>
											<span className="eq-fij-msg"><b>{f.autor.split(" ")[0]}:</b> {textoCorto(f.body, f.file_tipo, f.file_name, f.prospect_id)}</span></span>
									</button>
									{puedeSoltar ? <button className="eq-fij-x" onClick={() => fijar(f.id, f.paraTodas, false)} title="Desfijar" aria-label="Desfijar"><WappIcono n="x" /></button> : null}
								</div>
							);
						})() : null}

						{eligiendoIcono && puedeIcono ? (
							<IconoCanalPicker actual={canal.icono || null} canalId={canal.id} onCerrar={() => setEligiendoIcono(false)}
								onSubir={async (b) => { await http.prospectsService.equipoCanalIcono(campusId, canal.id, b); setEligiendoIcono(false); cargarMensajes(canal.id); cargarCanales(); }}
								onElegir={(e) => { setEligiendoIcono(false); accionCanal({ accion: "icono", icono: e }); }} />
						) : null}
						{info ? (
							<div className="eqm-info-velo" onClick={() => setInfo(null)}>
								<div className="eqm-info" role="dialog" aria-label="Info del mensaje" onClick={(e) => e.stopPropagation()}>
									<div className="eqm-info-cab"><b>Info del mensaje</b><button className="eqm-x" onClick={() => setInfo(null)} aria-label="Cerrar"><WappIcono n="x" /></button></div>
									{!info.datos ? <div className="apagado">Cargando…</div> : (
										<>
											<div className="eqm-info-resumen"><Palomitas estado={info.datos.leidos === info.datos.total && info.datos.total ? "leido" : info.datos.personas.every((p: any) => p.entregado) ? "entregado" : "enviado"} />
												{canal.tipo === "directo" ? (info.datos.leidos ? "Leído" : info.datos.personas.every((p: any) => p.entregado) ? "Entregado" : "Enviado") : "Leído por " + info.datos.leidos + " de " + info.datos.total}
												<span className="apagado"> · enviado {diaEtiqueta(info.datos.mensaje.created_at).toLowerCase()} {hora(info.datos.mensaje.created_at)}</span></div>
											<div className="eqm-info-lista">
												{info.datos.personas.map((p: any) => (
													<div key={p.id} className="eqm-info-fila">
														<span className="n">{p.nombre}</span>
														<span className="t">{p.leido ? <><Palomitas estado="leido" /> {p.leido_at ? diaEtiqueta(p.leido_at) + " " + hora(p.leido_at) : "Leído"}</>
															: p.entregado ? <><Palomitas estado="entregado" /> {p.entregado_at ? diaEtiqueta(p.entregado_at) + " " + hora(p.entregado_at) : "Entregado"}</>
															: <><Palomitas estado="enviado" /> Sin entregar</>}</span>
													</div>
												))}
											</div>
										</>
									)}
								</div>
							</div>
						) : null}
						<div className={"cv-chat eq-mensajes" + (sel ? " eligiendo" : "")} onClick={(e) => { setReaccionando(null); setFijando(null); if (!sel) tocarMencion(e); }} onScroll={alDesplazar}>
							{hayMas ? <button className="btn-wa" style={{ alignSelf: "center" }} onClick={() => cargarMensajes(canal.id, mensajes[0]?.id)}>Ver anteriores</button> : null}
							{!enCamino.vista.length ? <div className="ventas-vacio">{canal.puedoEscribir ? "Todavía no hay mensajes. Escribe el primero." : "Aquí salen los anuncios."}</div> : null}
							{(() => { const al = agruparAlbumes(enCamino.vista); albumCabeza = al.cabeza; albumDentro = al.dentro; return null; })()}
							{enCamino.vista.map((m) => {
								if (albumDentro.has(m.id)) return null;
								const album = albumCabeza.get(m.id);
								const dia = diaClave(m.created_at);
								const sep = dia !== ultimoDia ? <div className="cv-dia" role="separator"><span>{diaEtiqueta(m.created_at)}</span></div> : null;
								// La misma persona dentro de 10 minutos va pegada, sin repetir foto ni nombre.
								const ts = Date.parse(m.created_at.replace(" ", "T") + "Z");
								const seguido = dia === ultimoDia && ultimoAutor === m.user_id && ts - ultimoTs < 600000;
								ultimoDia = dia; ultimoAutor = m.user_id; ultimoTs = ts;
								const mio = m.user_id === yoId;
								const meMenciona = !!yoId && m.menciones.includes(yoId);
								// Como WappChat/WhatsApp: en canales y grupos, la de otras lleva avatar de color y su nombre del mismo tono.
								const conAvatar = !mio && canal.tipo !== "directo";
								const tono = "tono-" + (Math.abs(m.user_id || m.author_name.length) % 6);
								const tocarPersona = m.user_id ? (e: React.MouseEvent) => verPerfil(m.user_id, e) : undefined;
								return (
									<React.Fragment key={m.id}>
										{sep}
										{m.id === primerNuevo ? <div className="eq-nuevos" role="separator"><span>Nuevos</span></div> : null}
										<div data-eqm={m.id} className={"eq-fila-msg" + (mio ? " mio" : "") + (seguido ? " seguido" : "") + (conAvatar ? " av-movil" : "") + (elegidoMsg === m.id ? " elegido" : "") + (sel && sel[m.id] ? " sel" : "")}
											onClickCapture={sel && !m.deleted_at && !m.enCamino ? (e) => { e.preventDefault(); e.stopPropagation(); alternarSel(m, album || [m]); } : undefined}>
										{sel ? <MarcaSeleccion on={!!sel[m.id]} /> : null}
										{seguido ? <span className="eq-av-hueco" aria-hidden="true" />
											: <button type="button" tabIndex={-1} className={"eq-av " + tono + (fotos[m.user_id] ? " con-foto" : "")} onClick={tocarPersona} aria-label={"Perfil de " + m.author_name}>{fotos[m.user_id] ? <img src={fotos[m.user_id]} alt="" loading="lazy" /> : iniciales({ first_name: m.author_name.split(" ")[0], last_name: m.author_name.split(" ")[1] || "" })}</button>}
										
										<div onClick={(e) => { if (window.matchMedia("(max-width: 700px)").matches && !(e.target as HTMLElement).closest("button,a,video,audio")) setElegidoMsg(elegidoMsg === m.id ? null : m.id); }}
											onTouchStart={sel || m.deleted_at || m.enCamino ? undefined : (e) => presionar(m, e)} onTouchMove={soltarPresion} onTouchEnd={() => soltarPresion()} onTouchCancel={() => soltarPresion()}
											onClickCapture={(e) => { if (presion.current.id === m.id) { presion.current.id = null; e.preventDefault(); e.stopPropagation(); } }}
											onContextMenu={(e) => { if (window.matchMedia("(max-width: 700px)").matches) e.preventDefault(); }}
											className={"eq-msg" + (mio ? " mio" : "") + (seguido ? " seguido" : "") + (meMenciona ? " menciona" : "") + (esMedio(m, album) ? " con-medio" : "") + (m.file_tipo === "sticker" && !m.deleted_at ? " con-sticker" : "") + (m.reacciones.length ? " con-reac" : "")}>
											{!mio && !seguido && canal.tipo !== "directo" ? <span className={"eq-autor " + tono} role="button" tabIndex={-1} onClick={tocarPersona}>{m.author_name}</span> : null}
											{m.hilo_id && m.hilo_tambien && !m.deleted_at ? <button type="button" className="eq-en-hilo" onClick={(e) => { e.stopPropagation(); setHilo(m.hilo_id!); }}><WappIcono n="reply" />Respondió en un hilo</button> : null}
											{m.reenviado && !m.deleted_at ? <span className="eq-reenviado"><WappIcono n="forward" />Reenviado</span> : null}
											{m.deleted_at ? <p className="apagado"><i>Mensaje borrado</i></p> : (
												<>
													{m.reply_to_id ? <div className="eq-cita" role="button" tabIndex={0} title="Ir al mensaje" onClick={(e) => { e.stopPropagation(); irACita(m.reply_to_id); }}><b>{m.r_author}</b><span>{m.r_file_tipo || resumenTexto(m.r_body) ? textoCorto(m.r_body, m.r_file_tipo, null) : "Mensaje borrado"}</span></div> : null}
													{album ? <Album campusId={campusId} items={album} todos={mensajes} /> : m.file_tipo ? <MedioMensaje campusId={campusId} m={m} todos={mensajes} /> : null}
													{m.body ? (esMensajeLlamada(m.body) && !m.deleted_at
														? <BurbujaLlamada campusId={campusId} canalId={canal.id} body={m.body} hora={hora(m.created_at)} directo={canal.tipo === "directo"} viva={canal.llamada} onError={setError} />
														: m.encuesta ? <EncuestaMensaje campusId={campusId} m={m as any} esMia={mio} puedeCerrar={!!canal.soyAdmin} onCambio={() => cargarMensajes(canal.id, null, false)} />
														: <Texto t={m.body} />) : null}
													{m.prospect_id ? (
														<button className="eq-cliente" onClick={() => onAbrirCliente(m.prospect_id!)}>
															<span className={"av " + tonoAvatar(m.prospect_id)} aria-hidden="true">{iniciales({ first_name: m.p_first_name, last_name: m.p_last_name })}</span>
															<span><b>{nombreCompleto({ first_name: m.p_first_name, last_name: m.p_last_name })}</b>
																<small>{[m.p_status, m.p_curso, m.p_phone ? telefonoBonito(m.p_phone) : null].filter(Boolean).join(", ")}</small></span>
															<span className="abrir">Abrir chat</span>
														</button>
													) : null}
												</>
											)}
											<span className="h">{m.favorito && !m.deleted_at ? <WappIcono n="star" className="eq-fav-h" title="Favorito" /> : null}{m.edited_at && !m.deleted_at ? <span className="eq-h-edit">editado</span> : null}<span className="eq-h-hora">{hora(m.created_at)}</span>
												{m.enCamino ? <Reloj /> : mio && !m.importado && !m.deleted_at ? <Palomitas estado={estadoPalomitas(album ? album[album.length - 1] : m, vistoHasta, entregadoHasta)} onClick={() => verInfo(album ? album[album.length - 1] : m)} /> : null}
											</span>
											<PastillaReacciones reacciones={m.reacciones} onReaccionar={(e) => accionMensaje(m, "reaccion", e)} />
											{m.hilo_n && !m.hilo_id ? <ResumenHilo m={m} fotos={fotos} nuevas={hilosNuevas[m.id]} onAbrir={() => setHilo(m.id)} /> : null}
											{!m.deleted_at && !m.enCamino ? (
												<div className="eq-acciones" onClick={(e) => e.stopPropagation()}>
													<button title="Reaccionar" aria-label="Reaccionar" onClick={(e) => { setReacAbajo(barraAbajo(e.currentTarget)); setReaccionando(reaccionando === m.id ? null : m.id); }}><WappIcono n="smile" /></button>
													{canal.puedoEscribir ? <button title="Responder" aria-label="Responder" onClick={() => { setRespondiendo(m); setEditando(null); setElegidoMsg(null); caja.current?.focus(); }}><WappIcono n="reply" /></button> : null}
													{canal.puedoEscribir && !canal.supervision ? <button title="Responder en hilo" aria-label="Responder en hilo" onClick={() => { setElegidoMsg(null); setHilo(m.hilo_id || m.id); }}><WappIcono n="message" /></button> : null}
													{!canal.supervision ? <button title="Reenviar" aria-label="Reenviar" onClick={() => { setReenvioNuevo(false); setReenviando(album || [m]); setElegidoMsg(null); }}><WappIcono n="forward" /></button> : null}
													{/* Lo demás en "Más", como Slack (en celular todo sigue a la vista en la barra). */}
													<button className={"eq-acc-btn-mas" + (masMsg === m.id ? " on" : "")} title="Más" aria-label="Más acciones" aria-haspopup="menu" aria-expanded={masMsg === m.id}
														onClick={(e) => {
															// Cerca del fondo abre hacia arriba para que no lo tape la caja de escribir.
															const r = e.currentTarget.getBoundingClientRect(), c = e.currentTarget.closest(".eq-mensajes")?.getBoundingClientRect();
															setMasArriba(!!c && r.bottom + 300 > c.bottom);
															setMasMsg(masMsg === m.id ? null : m.id); setReaccionando(null);
														}}><WappIcono n="dots" /></button>
													<div className={"eq-acc-mas" + (masMsg === m.id ? " abierto" : "") + (masArriba ? " arriba" : "")} role="menu" onClick={() => setMasMsg(null)}>
													{!canal.supervision ? <button className={m.favorito ? "on" : ""} title={m.favorito ? "Quitar de Favoritos" : "Favorito"} aria-label={m.favorito ? "Quitar de Favoritos" : "Favorito"} aria-pressed={!!m.favorito}
														onClick={() => favorito(m, !m.favorito)}><WappIcono n="star" /><span className="eq-acc-lbl">{m.favorito ? "Quitar de Favoritos" : "Favorito"}</span></button> : null}
													{!canal.supervision ? (() => {
														const fijo = fijados.some((f) => f.id === m.id);
														const mio = fijados.some((f) => f.id === m.id && !f.paraTodas);
														// Sin poder fijar para todas, el botón solo se enciende con lo que fijó ella (lo de todas no lo puede soltar).
														return <button className={(canal.puedoFijarTodas ? fijo : mio) ? "on" : ""} title={canal.puedoFijarTodas ? "Fijar" : mio ? "Desfijar" : "Fijar Solo Para Mí"} aria-label="Fijar" aria-haspopup={canal.puedoFijarTodas ? "menu" : undefined}
															onClick={(e) => { e.stopPropagation(); setMasMsg(null); if (canal.puedoFijarTodas) setFijando(fijando === m.id ? null : m.id); else fijar(m.id, false, !mio); }}><WappIcono n="fijar" /><span className="eq-acc-lbl">{fijo ? "Desfijar" : "Fijar"}</span></button>;
													})() : null}
													{m.file_tipo === "sticker" && !canal.supervision ? <button title="Guardar en Mis Stickers" aria-label="Guardar en Mis Stickers"
														onClick={() => { setElegidoMsg(null); guardarSticker(() => http.prospectsService.equipoGuardarSticker(campusId, m.id)); }}><IconoSticker /><span className="eq-acc-lbl">Guardar Sticker</span></button> : null}
													{m.body && resumenTexto(m.body) ? <button title="Copiar" aria-label="Copiar" onClick={() => { copiarTexto(resumenTexto(m.body)); setElegidoMsg(null); }}><WappIcono n="copy" /><span className="eq-acc-lbl">Copiar Texto</span></button> : null}
													<button title="Seleccionar" aria-label="Seleccionar" onClick={() => { setSel({ [m.id]: album || [m] }); setElegidoMsg(null); setReaccionando(null); }}><WappIcono n="check" /><span className="eq-acc-lbl">Seleccionar</span></button>
													{mio && m.body && gifDe(m.body)?.resto !== "" ? <button title="Editar" aria-label="Editar" onClick={() => { setEditando(m); setRespondiendo(null); borrador.soloPantalla(m.body_crudo || m.body || ""); caja.current?.focus(); }}><WappIcono n="edit" /><span className="eq-acc-lbl">Editar</span></button> : null}
													{mio && !m.importado ? <button title="Info del mensaje" aria-label="Info del mensaje" onClick={() => verInfo(m)}><WappIcono n="info" /><span className="eq-acc-lbl">Info del Mensaje</span></button> : null}
													{!canal.supervision && !m.importado ? <button title="Recordarme" aria-label="Recordarme" onClick={(e) => { e.stopPropagation(); setMasMsg(null); setRecordando(m.id); }}><WappIcono n="clock" /><span className="eq-acc-lbl">Recordarme…</span></button> : null}
													{!canal.supervision && !mio ? <button title="Marcar como no leído" aria-label="Marcar como no leído" onClick={() => noLeidoDesde(m)}><WappIcono n="eye" /><span className="eq-acc-lbl">Marcar No Leído</span></button> : null}
													{(mio || canal.soyAdmin) && !m.sin_borrar ? <button className="peligro" title="Borrar" aria-label="Borrar" onClick={() => { if (window.confirm("¿Borrar este mensaje para todas? No se puede deshacer.")) accionMensaje(m, "borrar"); }}><WappIcono n="trash" /><span className="eq-acc-lbl">Borrar</span></button> : null}
													</div>
												</div>
											) : null}
											{recordando === m.id ? <MenuHoras titulo="Recordarme este mensaje" opciones={OPCIONES_RECORDAR} className={"eq-recordar-menu" + (mio ? " mio" : "") + (masArriba ? " arriba" : "")}
												onCerrar={() => setRecordando(null)} onElegir={(iso) => recordar(m, iso)} /> : null}
											{fijando === m.id ? (() => {
												const todas = fijados.some((f) => f.id === m.id && f.paraTodas);
												const mia = fijados.some((f) => f.id === m.id && !f.paraTodas);
												const quienes = canal.tipo === "directo" ? "Para los Dos" : "Para Todas";
												return (
													<div className="eq-fijar-menu" role="menu" onClick={(e) => e.stopPropagation()}>
														<button role="menuitem" onClick={() => fijar(m.id, true, !todas)}><WappIcono n="users" />{(todas ? "Desfijar " : "Fijar ") + quienes}</button>
														<button role="menuitem" onClick={() => fijar(m.id, false, !mia)}><WappIcono n="user" />{mia ? "Desfijar Solo Para Mí" : "Fijar Solo Para Mí"}</button>
													</div>
												);
											})() : null}
											{reaccionando === m.id ? (
												<div className={"eq-emojis" + (reacAbajo ? " abajo" : "")} onClick={(e) => e.stopPropagation()}>{EMOJIS.map((e) => <button key={e} onClick={() => accionMensaje(m, "reaccion", e)}>{e}</button>)}
													<button className="eqe-mas" title="Más emojis" aria-label="Más emojis" onClick={() => { setReaccionando(null); setMasReac(m); }}>+</button></div>
											) : null}
										</div>
										</div>
									</React.Fragment>
								);
							})}
							{escribe ? <span className="eq-escribe">{escribe} está escribiendo…</span> : null}
							{lejos ? (
								<button type="button" className={"ex-abajo" + (nuevosAbajo ? " con-nuevos" : "")} onClick={(e) => { e.stopPropagation(); fin.current?.scrollIntoView({ block: "end", behavior: "smooth" }); }}
									aria-label={nuevosAbajo ? nuevosAbajo + " mensajes nuevos: ir abajo" : "Ir al último mensaje"}>
									{nuevosAbajo ? <span>{nuevosAbajo === 1 ? "1 mensaje nuevo" : nuevosAbajo + " mensajes nuevos"}</span> : null}
									<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M12 5v13M6 12l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
								</button>
							) : null}
							<div ref={fin} />
						</div>

						{canal.supervision ? <div className="eq-solo-lectura">Estás leyendo una conversación de tu equipo. No puedes escribir aquí.</div> : null}
						{sel ? (
							<BarraSeleccion n={Object.keys(sel).length} texto={textoSel} onCancelar={() => setSel(null)}
								onReenviar={canal.supervision ? null : () => { const l = elegidosSel(); if (l.length) { setReenvioNuevo(true); setReenviando(l); } }} />
						) : canal.puedoEscribir ? (
							<div className="cv-compositor eq-compositor">
								{respondiendo || editando ? (
									<div className="eq-respondiendo">
										<span><b>{editando ? "Editando tu mensaje" : "Respondiendo a " + respondiendo!.author_name.split(" ")[0]}</b>
											{respondiendo ? <small>{textoCorto(respondiendo.body, respondiendo.file_tipo, respondiendo.file_name, respondiendo.prospect_id)}</small> : null}</span>
										<button onClick={() => { setRespondiendo(null); if (editando) { setEditando(null); borrador.restaurar(); } }} aria-label="Cancelar">×</button>
									</div>
								) : null}
								<ListaMenciones sugeridas={men.sugeridas} activa={men.activa} onActiva={men.setActiva} onElegir={elegirMencion} />
								{okTexto ? <div className="aviso ok ex-ok" role="status">{okTexto}</div> : null}
								{texto.length > 3500 ? <div className={"ex-largo" + (texto.length >= 4000 ? " tope" : "")} role="status">{texto.length.toLocaleString("es-MX")} de 4,000 letras{texto.length >= 4000 ? ": ya no cabe más en un mensaje" : ""}</div> : null}
								{programados.length ? (
									<div className="ex-prog">
										<button type="button" className="ex-prog-bar" onClick={() => setVerProgramados(!verProgramados)} aria-expanded={verProgramados}>
											<WappIcono n="clock" />{programados.length === 1 ? "1 mensaje programado aquí" : programados.length + " mensajes programados aquí"}<span>{verProgramados ? "Ocultar" : "Ver"}</span></button>
										{verProgramados ? programados.map((p) => (
											<div key={p.id} className="ex-prog-item">
												<span><small>{fechaProgramada(p.cuando)}{p.hilo_id ? " · en un hilo" : ""}</small>{p.body}</span>
												<button type="button" className="enlace" onClick={() => cancelarProgramado(p.id, true)}>Editar</button>
												<button type="button" className="enlace peligro" onClick={() => cancelarProgramado(p.id, false)}>Cancelar</button>
											</div>
										)) : null}
									</div>
								) : null}
								{error ? <div className="aviso error" role="alert">{error}<button type="button" aria-label="Cerrar aviso" onClick={() => setError(null)}
									style={{ marginLeft: 10, background: "none", border: 0, color: "inherit", fontSize: 18, lineHeight: 1, cursor: "pointer" }}>×</button></div> : null}
								{elegidos.length ? (
									<BandejaEnvio elegidos={elegidos} onQuitar={quitarArchivo} onAgregar={agregarArchivos} onCancelar={cancelarArchivos}
										onEnviar={enviarArchivos} progreso={progresoArch} error={errorArch} mencionar={{ campusId, canalId: canal.id }} />
								) : null}
								{formatoBar && !elegidos.length && !adjOcupado ? (
									<div className="ex-formato" role="toolbar" aria-label="Formato" onMouseDown={(e) => e.preventDefault()}>
										<button type="button" onClick={() => envolver("*")} title="Negritas (Cmd+B)" aria-label="Negritas"><b>B</b></button>
										<button type="button" onClick={() => envolver("_")} title="Cursiva (Cmd+I)" aria-label="Cursiva"><i>I</i></button>
										<button type="button" onClick={() => envolver("~")} title="Tachado (Cmd+Shift+X)" aria-label="Tachado"><s>S</s></button>
										<span className="ex-formato-sep" aria-hidden="true" />
										<button type="button" onClick={() => envolver("`")} title="Código" aria-label="Código"><code>{"</>"}</code></button>
										<button type="button" onClick={() => envolver("", true)} title="Lista" aria-label="Lista">• —</button>
									</div>
								) : null}
								{/* Misma barra que WappChat (Esteban, 2026-10-15): clip con su menú y micrófono, emoji dentro del cuadro y avión redondo. */}
								<div className={"cv-escribir" + (adjOcupado ? " con-adjunto" : "") + (texto.trim() ? " escribiendo" : "")} style={elegidos.length ? { display: "none" } : undefined}>
									{!editando ? (
										<Adjuntos alElegir={agregarArchivos} campusId={campusId} prospectId={0} channelId={null} puede={true} onOcupado={setAdjOcupado}
											onEnviado={() => { setRespondiendo(null); cargarMensajes(canal.id); cargarCanales(); }}
											enviar={async (file, name, kind, caption, onProgress) => {
												await http.prospectsService.equipoArchivoSubir(campusId, canal.id, file, name, kind, caption, { replyTo: respondiendo?.id || null, onProgress });
											}} />
									) : null}
									{adjOcupado ? null : (
										<>
											{/* Emoji y GIF dentro de la barra, como WhatsApp (Esteban, 2026-10-12). */}
											<div className="wb-campo">
											<BotonEmoji enBarra onElegir={(em) => insertarEnCursor(caja.current, texto, em, setTexto)} disabled={busy}
												stickers={!editando ? { campusId, onEnviar: enviarSticker } : undefined} />
											<textarea ref={caja} rows={1} value={texto} aria-label="Mensaje al equipo" maxLength={4000}
												placeholder={window.innerWidth < 400 ? "Mensaje" : canal.anuncios ? "Escribe un anuncio" : "Escribe un mensaje" + (canal.tipo !== "directo" && window.innerWidth > 700 ? " (@ para mencionar)" : "")}
												onChange={(e) => alEscribir(e.target.value, e.target.selectionStart)}
												onKeyDown={(e) => {
													if (men.tecla(e, elegirMencion)) return;
													if ((e.metaKey || e.ctrlKey) && !e.altKey) {
														const k = e.key.toLowerCase();
														if (k === "b" || k === "i" || (k === "x" && e.shiftKey)) { e.preventDefault(); envolver(k === "b" ? "*" : k === "i" ? "_" : "~"); return; }
													}
													if (e.key === "Escape") { setRespondiendo(null); if (editando) { setEditando(null); borrador.restaurar(); } }
													// ↑ con el cuadro vacío: editar mi último mensaje, como Slack.
													if (e.key === "ArrowUp" && !texto && !editando && ultimoMio && ultimoMio.body && !ultimoMio.enCamino) {
														e.preventDefault(); setEditando(ultimoMio); setRespondiendo(null); borrador.soloPantalla(ultimoMio.body_crudo || ultimoMio.body || ""); return;
													}
													teclaEnter(e, enviar);
												}} />
											{!editando ? <BotonGif enBarra campusId={campusId} onElegir={enviarGif} disabled={busy} /> : null}
											</div>
											{!editando ? (
												<div className="ex-comp-extra">
													<button type="button" className={"ex-comp-btn ex-aa" + (formatoBar ? " on" : "")} title="Formato (negritas, cursiva…)" aria-label="Formato" aria-pressed={formatoBar}
														onMouseDown={(e) => e.preventDefault()} onClick={alternarFormato}>Aa</button>
													{texto.trim() ? <button type="button" className={"ex-comp-btn" + (programando ? " on" : "")} title="Enviar más tarde" aria-label="Enviar más tarde" onClick={() => setProgramando(!programando)}><WappIcono n="clock" /></button>
														: <button type="button" className="ex-comp-btn" title="Encuesta" aria-label="Nueva encuesta" onClick={() => setEncuestaNueva(true)}><span aria-hidden="true">📊</span></button>}
													{programando ? <MenuHoras titulo="Enviar más tarde" opciones={OPCIONES_PROGRAMAR} className="ex-prog-menu" onCerrar={() => setProgramando(false)} onElegir={programarEnvio} /> : null}
												</div>
											) : null}
											{editando ? <button className="cv-enviar wa" disabled={busy || !texto.trim()} onClick={enviar}>{busy ? "…" : "Guardar"}</button> : (
												<button className="cv-enviar wa cv-avion" disabled={!texto.trim()} onClick={enviar} onMouseDown={(e) => e.preventDefault() /* no le quita el foco al cuadro: el teclado del celular no se cierra */} aria-label="Enviar" title="Enviar (Enter) · Salto de línea: Ctrl + Enter o Shift + Enter">
													{busy ? "…" : <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path fill="currentColor" d="M3.4 20.4 21 12 3.4 3.6 3.4 10l12.6 2-12.6 2z" /></svg>}
												</button>
											)}
										</>
									)}
								</div>
							</div>
						) : !canal.supervision ? <div className="eq-solo-leer">Canal de anuncios: solo las administradoras escriben. Puedes reaccionar.</div> : null}
					</div>
				) : <div className="ventas-cliente"><div className="ventas-vacio">Elige un canal o una conversación.</div></div>}
				{abierto && canal && (panel === "miembros" || panel === "ajustes") && !hilo ? (
					<aside className="hp eq-lado-personas" aria-label={panel === "ajustes" ? "Ajustes" : "Personas"}>
						<div className="hp-barra"><div className="hp-titulo"><b>{panel === "ajustes" ? "Ajustes" : "Personas"}</b><span>{canal.tipo === "canal" ? "# " : ""}{canal.nombre || canales.find((c) => c.id === canal.id)?.nombre}</span></div>
							<button type="button" className="hp-ico" onClick={() => setPanel(null)} aria-label="Cerrar" title="Cerrar (Esc)"><WappIcono n="x" /></button></div>
						<div className="hp-lista eq-miembros">
								{panel === "ajustes" ? (
									<div className="eq-ajustes">
										<div className="eq-ajustes-cab"><h4>Ajustes del Canal</h4>
											<button type="button" className="btn-wa eq-ajustes-icono" onClick={() => setEligiendoIcono(true)}>
												<span className={"eq-cab-av" + (canal.icono ? " eq-con-emoji" : "")} aria-hidden="true">{icono(canal)}</span>Cambiar Ícono</button></div>
										{!canal.automatico ? <input defaultValue={canal.nombre} onBlur={(e) => {
											const v = e.target.value.trim();
											if (v === canal.nombre) return;
											if (v.length < 2) { setError("El nombre del canal necesita al menos 2 letras."); e.target.value = canal.nombre; return; }
											accionCanal({ accion: "editar", nombre: v });
										}} aria-label="Nombre del canal" /> : null}
										<input defaultValue={canal.descripcion || ""} placeholder="Descripción" onBlur={(e) => accionCanal({ accion: "editar", descripcion: e.target.value })} aria-label="Descripción" />
										<label className="eq-check"><input type="checkbox" checked={canal.anuncios} onChange={(e) => accionCanal({ accion: "editar", anuncios: e.target.checked })} /> De anuncios: solo las administradoras escriben</label>
										{!canal.automatico ? <label className="eq-check"><input type="checkbox" checked={canal.visibilidad === "publico"} onChange={(e) => accionCanal({ accion: "editar", visibilidad: e.target.checked ? "publico" : "privado" })} /> Público</label> : null}
									</div>
								) : null}
								{(canal.tipo === "grupo" || (canal.tipo === "canal" && canal.soyAdmin)) ? (
									<>
										<h4>Agregar Personas</h4>
										{listaPersonas((p) => accionCanal({ accion: "agregar", userIds: [p.id] }), miembros.map((m) => m.id), false)}
									</>
								) : null}
								<h4>Personas ({miembros.length})</h4>
								{miembros.map((p) => (
									<div key={p.id} className="eq-miembro">
										<button type="button" className="eq-miembro-quien" onClick={(e) => verPerfil(p.id, e)} title="Ver perfil">
											<span className={"eq-av " + tonoAvatar(p.id) + (fotos[p.id] ? " con-foto" : "")} aria-hidden="true">{fotos[p.id] ? <img src={fotos[p.id]} alt="" loading="lazy" /> : iniciales({ first_name: p.nombre.split(" ")[0], last_name: p.nombre.split(" ")[1] || "" })}</span>
											<span>{p.nombre}</span>{p.admin ? <span className="chip">Administradora</span> : null}</button>
										{p.id !== yoId && canal.tipo === "grupo" && (canal.puedoQuitar ?? (canal.soyAdmin || canal.soyCreador)) ? (
											<span className="fila-botones"><button className="enlace" onClick={() => { if (window.confirm("¿Sacar a " + p.nombre + " del grupo?")) accionCanal({ accion: "quitar", userId: p.id }); }}>Quitar</button></span>
										) : null}
										{canal.soyAdmin && canal.tipo === "canal" && p.id !== yoId ? (
											<span className="fila-botones">
												<button className="enlace" onClick={() => accionCanal({ accion: p.admin ? "quitarAdmin" : "admin", userId: p.id })}>{p.admin ? "Quitar admin" : "Hacer admin"}</button>
												{!canal.automatico ? <button className="enlace" onClick={() => { if (window.confirm("¿Quitar a " + p.nombre + " de " + canal.nombre + "?")) accionCanal({ accion: "quitar", userId: p.id }); }}>Quitar</button> : null}
											</span>
										) : null}
									</div>
								))}
						</div>
					</aside>
				) : null}
				{abierto && canal && adjuntos && !hilo && panel !== "miembros" && panel !== "ajustes" ? (
					<AdjuntosPanel campusId={campusId} canalId={canal.id} onCerrar={() => setAdjuntos(false)} onIr={(id, h) => { if (h) setHilo(h); else irAMensaje(canal.id, id); }} />
				) : null}
				{abierto && canal && hilo ? (
					<HiloPanel campusId={campusId} hiloId={hilo} version={hiloVersion} yoId={yoId} puedoEscribir={!!canal.puedoEscribir && !canal.supervision}
						donde={(canal.tipo === "canal" ? "# " : "") + (canal.tipo === "canal" ? canal.nombre : canales.find((c) => c.id === canal.id)?.nombre || canal.nombre)}
						onCerrar={() => setHilo(null)} onPerfil={verPerfil} onCambio={() => { if (abierto) cargarMensajes(abierto, null, false); cargarHilos(); }} />
				) : null}
			</div>
			{encuestaNueva && abierto ? <EncuestaModal campusId={campusId} canalId={abierto} onCerrar={() => setEncuestaNueva(false)} onCreada={() => { cargarMensajes(abierto); cargarCanales(); }} /> : null}
			{salto ? <SaltoRapido canales={canales} onCerrar={() => setSalto(false)} onElegir={(id) => { setVistaHilos(false); setVistaActividad(false); setVistaIa(false); setAbierto(id); }}
				onAtajo={(k) => { setAbierto(null); setVistaIa(false); if (k === "hilos") { setVistaActividad(false); setVistaHilos(true); cargarHilos(); } else { setVistaHilos(false); setVistaActividad(true); } }} /> : null}
			{tarjeta ? <PerfilPersona campusId={campusId} ancla={tarjeta} onCerrar={() => setTarjeta(null)}
				onMensaje={(id) => { setTarjeta(null); directo(id); }} onAbrirCanal={(id) => { setTarjeta(null); setPanel(null); setAbierto(id); }} /> : null}
			{masReac ? <EmojisFlotante onElegir={(e) => { const m = masReac; setMasReac(null); accionMensaje(m, "reaccion", e); }} onCerrar={() => setMasReac(null)} /> : null}
			{reenviando ? <EquipoReenviar campusId={campusId} mensajes={reenviando} nuevo={reenvioNuevo} onCerrar={() => { setReenviando(null); if (reenvioNuevo) setSel(null); }}
				onEnviado={(ids) => { cargarCanales(); if (abiertoRef.current && ids.includes(abiertoRef.current)) cargarMensajes(abiertoRef.current); }} /> : null}
		</div>
	);
};

export default EquipoView;
